import { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { InsertImageButton, RichNote } from '@/components/SupportImages';
import { toast } from 'sonner';
import { ClipboardText, Plus, PencilSimple, Flame, Barbell, Drop, Grains, Leaf, Trash, MagnifyingGlass, FilePdf, Copy, NotePencil, Clock, Swap, CaretUp, CaretDown, DotsSixVertical, Target, CheckCircle, ArrowUp, ArrowDown, FloppyDisk, Warning } from '@phosphor-icons/react';
import ShareButton from '@/components/ShareButton';
import { Md } from '@/components/Md';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useAuth } from '@/contexts/AuthContext';
import { ImportMealPlanDialog } from '@/components/ImportDialogs';
import PatientSearch from '@/components/PatientSearch';
import { useConfirmDelete } from '@/components/ConfirmDialogs';
import { fmtDay, fmtStamp } from '@/utils/dateFmt';

const API = process.env.REACT_APP_BACKEND_URL;

// ─── DYNAMIC EQUIVALENTS EDITOR (Nutrium-style) ───
function DynamicEquivEditor({ referenceFood, currentEquivs, onSave }) {
  const [groups, setGroups] = useState([]);
  const [equivs, setEquivs] = useState(currentEquivs || []);
  const [nutrient, setNutrient] = useState('hidratos_g');
  const [searchQ, setSearchQ] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [mode, setMode] = useState('dynamic'); // dynamic | groups
  const [refData, setRefData] = useState(null);
  const [refMeasures, setRefMeasures] = useState([]);

  useEffect(() => { (async () => { try { const { data } = await axios.get(`${API}/api/equivalent-groups`); setGroups(data); } catch {} })(); }, []);
  // Fetch reference food full data + measures if missing nutritional values
  useEffect(() => {
    if (!referenceFood?.food_id) return;
    (async () => {
      try {
        const [fRes, mRes] = await Promise.all([
          axios.get(`${API}/api/foods?search=&limit=200`),
          axios.get(`${API}/api/foods/${referenceFood.food_id}/measures`),
        ]);
        const found = (fRes.data || []).find(f => f.id === referenceFood.food_id);
        if (found) setRefData(found);
        setRefMeasures(mRes.data || []);
      } catch {}
    })();
  }, [referenceFood?.food_id]);
  useEffect(() => {
    if (!searchQ || searchQ.length < 2) { setSearchResults([]); return; }
    const t = setTimeout(async () => { try { const { data } = await axios.get(`${API}/api/foods?search=${encodeURIComponent(searchQ)}&limit=6`); setSearchResults(data.filter(f => f.id !== referenceFood?.food_id)); } catch {} }, 200);
    return () => clearTimeout(t);
  }, [searchQ, referenceFood]);

  const addAndCalc = async (food) => {
    if (equivs.some(e => e.food_id === food.id)) return;
    try {
      const { data } = await axios.post(`${API}/api/equivalent-groups/calculate`, {
        reference_food_id: referenceFood.food_id, reference_quantity: parseFloat(referenceFood.quantity) || 100,
        nutrient, alternative_food_ids: [food.id],
      });
      const r = data.results?.[0];
      setEquivs([...equivs, { food_id: food.id, food_name: food.name, quantity: r?.equivalent_portion ? String(r.equivalent_portion) : '', unit: 'g', energia_kcal: food.energia_kcal, proteina_g: food.proteina_g, hidratos_g: food.hidratos_g, gordura_g: food.gordura_g, error: r?.error || null, measure_display: r?.measure_display || null, measure_name: r?.measure_name || null, measure_qty: r?.measure_qty || null }]);
    } catch {
      setEquivs([...equivs, { food_id: food.id, food_name: food.name, quantity: '', unit: 'g', error: 'Erro no cálculo' }]);
    }
    setSearchQ(''); setSearchResults([]);
  };

  const recalcAll = async () => {
    if (equivs.length === 0) return;
    try {
      const { data } = await axios.post(`${API}/api/equivalent-groups/calculate`, {
        reference_food_id: referenceFood.food_id, reference_quantity: parseFloat(referenceFood.quantity) || 100,
        nutrient, alternative_food_ids: equivs.map(e => e.food_id),
      });
      setEquivs(equivs.map(e => { const r = data.results?.find(res => res.food_id === e.food_id); return r ? { ...e, quantity: r.equivalent_portion ? String(r.equivalent_portion) : e.quantity, error: r.error || null, measure_display: r.measure_display || null, measure_name: r.measure_name || null, measure_qty: r.measure_qty || null } : e; }));
    } catch {}
  };

  const applyGroup = (g) => {
    setEquivs((g.foods || []).map(f => ({ food_id: f.food_id, food_name: f.food_name, quantity: String(f.equivalent_portion || ''), unit: f.unit || 'g' })));
    setMode('dynamic');
  };

  const updateQty = (i, q) => { const u = [...equivs]; u[i] = { ...u[i], quantity: q }; setEquivs(u); };
  const updateUnit = (i, unit) => { const u = [...equivs]; u[i] = { ...u[i], unit }; setEquivs(u); };
  const remove = (i) => setEquivs(equivs.filter((_, j) => j !== i));

  // Auto-recalculate when nutrient changes
  const prevNutrient = useState(nutrient)[0];
  useEffect(() => {
    if (equivs.length > 0 && nutrient !== 'hidratos_g' || equivs.length > 0) {
      // Recalculate on nutrient change (skip initial)
      const timer = setTimeout(() => { recalcAll(); }, 100);
      return () => clearTimeout(timer);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nutrient]);

  const NUTRIENTS = [{ v: 'hidratos_g', l: 'HC' }, { v: 'proteina_g', l: 'Prot' }, { v: 'gordura_g', l: 'Gord' }, { v: 'energia_kcal', l: 'kcal' }];
  // Calc reference macros - use refData from API as fallback
  const refQty = parseFloat(referenceFood?.quantity) || 100;
  const refFactor = refQty / 100;
  const ekcal = referenceFood?.energia_kcal_100 || refData?.energia_kcal || 0;
  const prot = referenceFood?.proteina_g_100 || refData?.proteina_g || 0;
  const hc = referenceFood?.hidratos_g_100 || refData?.hidratos_g || 0;
  const gord = referenceFood?.gordura_g_100 || refData?.gordura_g || 0;
  const refMacros = {
    e: Math.round(ekcal * refFactor * 10) / 10,
    p: Math.round(prot * refFactor * 10) / 10,
    h: Math.round(hc * refFactor * 10) / 10,
    g: Math.round(gord * refFactor * 10) / 10,
  };

  return (
    <div className="space-y-4">
      {/* Mode toggle */}
      <div className="flex gap-2">
        <button onClick={() => setMode('dynamic')} className={`flex-1 py-2 rounded-xl text-xs font-medium ${mode === 'dynamic' ? 'bg-brand-green text-white' : 'bg-brand-bg text-brand-muted border border-brand-border'}`}>Cálculo dinâmico</button>
        <button onClick={() => setMode('groups')} className={`flex-1 py-2 rounded-xl text-xs font-medium ${mode === 'groups' ? 'bg-brand-green text-white' : 'bg-brand-bg text-brand-muted border border-brand-border'}`}>Grupos guardados</button>
      </div>

      {mode === 'groups' && groups.length > 0 && (
        <div className="space-y-2 max-h-40 overflow-y-auto">
          {groups.map(g => (
            <button key={g.id} onClick={() => applyGroup(g)} className="w-full text-left p-3 rounded-xl border border-brand-border hover:border-brand-green/30 transition-all">
              <p className="text-sm font-medium text-brand-text">{g.name}</p>
              <div className="flex flex-wrap gap-1 mt-1">{(g.foods || []).slice(0, 3).map((f, i) => <span key={i} className="text-[10px] bg-brand-mint/15 text-brand-green px-2 py-0.5 rounded-full">{f.equivalent_portion}{f.unit} {f.food_name}</span>)}</div>
            </button>
          ))}
        </div>
      )}

      {mode === 'dynamic' && (
        <>
          {/* Reference food */}
          <div className="bg-brand-green/5 border border-brand-green/15 rounded-xl p-3">
            <p className="text-[10px] text-brand-green font-semibold uppercase tracking-wider mb-1">Referência</p>
            <p className="text-sm font-medium text-brand-text">{referenceFood?.display_name || referenceFood?.food_name}</p>
            <p className="text-xs text-brand-muted">{referenceFood?.quantity} {referenceFood?.unit}</p>
            <div className="flex gap-3 mt-1 text-[10px] text-brand-muted">
              <span>{refMacros.e} kcal</span><span>P:{refMacros.p}g</span><span>HC:{refMacros.h}g</span><span>G:{refMacros.g}g</span>
            </div>
            {refMeasures.length > 0 && (
              <div className="mt-2 pt-2 border-t border-brand-green/10">
                <p className="text-[9px] text-brand-muted mb-1">Medidas caseiras:</p>
                <div className="flex flex-wrap gap-1">
                  {refMeasures.map((m, i) => (
                    <span key={i} className="text-[9px] bg-brand-green/10 text-brand-green px-2 py-0.5 rounded-full">
                      1 {m.measure_name} = {m.grams_equivalent}g
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Nutrient selector */}
          <div>
            <p className="text-[10px] text-brand-muted mb-1.5">Calcular equivalência por:</p>
            <div className="flex gap-1.5">
              {NUTRIENTS.map(n => <button key={n.v} onClick={() => { setNutrient(n.v); setTimeout(recalcAll, 100); }} className={`px-3 py-1.5 rounded-full text-xs font-medium ${nutrient === n.v ? 'bg-brand-green text-white' : 'bg-brand-bg text-brand-muted border border-brand-border'}`}>{n.l}</button>)}
            </div>
          </div>

          {/* Alternatives table */}
          {equivs.length > 0 && (
            <div className="border border-brand-border rounded-xl overflow-hidden">
              <div className="grid grid-cols-[1fr_55px_45px_40px_40px_40px_28px] gap-0 text-[9px] text-brand-muted bg-brand-bg p-2 font-medium">
                <span>Alimento</span><span className="text-center">Qtd</span><span className="text-center">kcal</span><span className="text-center">P</span><span className="text-center">HC</span><span className="text-center">G</span><span></span>
              </div>
              {equivs.map((e, i) => {
                const eq = parseFloat(e.quantity) || 0;
                const ef = eq / 100;
                const em = { e: Math.round((e.energia_kcal || 0) * ef * 10) / 10, p: Math.round((e.proteina_g || 0) * ef * 10) / 10, h: Math.round((e.hidratos_g || 0) * ef * 10) / 10, g: Math.round((e.gordura_g || 0) * ef * 10) / 10 };
                return (
                  <div key={i} className="grid grid-cols-[1fr_55px_45px_40px_40px_40px_28px] gap-0 items-center p-2 border-t border-brand-border/30 text-xs">
                    <div>
                      <span className="text-[10px] text-brand-green font-medium">ou </span>
                      <input value={e.display_name || e.food_name} onChange={ev => { const u = [...equivs]; u[i] = { ...u[i], display_name: ev.target.value }; setEquivs(u); }} className="bg-transparent text-brand-text text-xs border-b border-transparent hover:border-brand-border focus:border-brand-green focus:outline-none w-auto" title="Editar nome apresentado" />
                      {e.measure_display && <p className="text-[9px] text-brand-gold-hover font-medium mt-0.5">{e.measure_display}</p>}
                      {e.error && <p className="text-[9px] text-red-500">{e.error}</p>}
                    </div>
                    <input value={e.quantity} onChange={ev => updateQty(i, ev.target.value)} className="w-full text-center text-xs border border-brand-border/50 rounded px-1 py-0.5 bg-white" />
                    <span className="text-center text-[10px] text-brand-muted">{em.e}</span>
                    <span className="text-center text-[10px] text-brand-muted">{em.p}</span>
                    <span className="text-center text-[10px] text-brand-muted">{em.h}</span>
                    <span className="text-center text-[10px] text-brand-muted">{em.g}</span>
                    <button onClick={() => remove(i)} className="text-red-400 hover:text-red-600 text-center"><Trash size={11} /></button>
                  </div>
                );
              })}
            </div>
          )}

          {/* Add alternative */}
          <div className="relative">
            <Input value={searchQ} onChange={e => setSearchQ(e.target.value)} placeholder="Procurar alimento alternativo..." className="rounded-xl border-brand-border text-sm" data-testid="equiv-search" />
            {searchResults.length > 0 && (
              <div className="absolute z-20 top-full left-0 right-0 mt-1 bg-white border border-brand-border rounded-xl shadow-lg max-h-36 overflow-y-auto">
                {searchResults.map(f => (
                  <button key={f.id} type="button" onClick={() => addAndCalc(f)} className="w-full text-left px-3 py-2 text-xs hover:bg-brand-mint/10 border-b border-brand-border/30 last:border-0">
                    <span className="font-medium text-brand-text">{f.name}</span>
                    <span className="text-[9px] text-brand-muted ml-1">{f.energia_kcal}kcal P:{f.proteina_g}g HC:{f.hidratos_g}g</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </>
      )}

      {/* Preview */}
      {equivs.length > 0 && (
        <div className="bg-brand-mint/10 border border-brand-mint/20 rounded-xl p-3">
          <p className="text-[10px] text-brand-green font-semibold uppercase tracking-wider mb-1.5">Resultado para a cliente</p>
          <p className="text-xs text-brand-text font-medium mb-1">{referenceFood?.quantity} {referenceFood?.unit} {referenceFood?.display_name || referenceFood?.food_name}</p>
          {equivs.filter(e => e.quantity).map((e, i) => (
            <div key={i} className="mb-0.5">
              <p className="text-xs text-brand-text">
                <span className="text-brand-green font-medium">ou </span>
                {e.measure_display ? (
                  <><b>{e.measure_display}</b> de {e.display_name || e.food_name} <span className="text-[10px] text-brand-muted">({e.quantity}g)</span></>
                ) : (
                  <>{e.quantity}g {e.display_name || e.food_name}</>
                )}
              </p>
            </div>
          ))}
        </div>
      )}

      <button onClick={() => onSave(equivs.filter(e => e.food_name).map(({ error, energia_kcal, proteina_g, hidratos_g, gordura_g, ...rest }) => ({ ...rest, food_name: rest.display_name || rest.food_name, measure_display: rest.measure_display || null })))} className="w-full bg-brand-green text-white rounded-full py-3 font-medium" data-testid="save-equivalents">Guardar equivalentes</button>
    </div>
  );
}

// Inline editable equiv name - saves on blur, not on every keystroke
function EquivNameField({ eq, isNutri, onSave, testId }) {
  const [val, setVal] = useState(eq.food_name || '');
  const [dirty, setDirty] = useState(false);
  useEffect(() => { setVal(eq.food_name || ''); }, [eq.food_name]);
  return (
    <div className="flex items-center gap-1.5">
      <span className="text-[10px] text-brand-green font-medium">ou</span>
      {eq.measure_display ? (
        <span className="text-xs text-brand-text"><b>{eq.measure_display}</b></span>
      ) : (
        <span className="text-xs text-brand-text"><b>{eq.quantity}{eq.unit}</b></span>
      )}
      {isNutri ? (
        <input
          value={val}
          onChange={e => { setVal(e.target.value); setDirty(true); }}
          onBlur={() => { if (dirty) { onSave(val); setDirty(false); } }}
          onKeyDown={e => { if (e.key === 'Enter') { e.target.blur(); } }}
          className="text-xs text-brand-text bg-transparent border-b border-dashed border-brand-border/50 hover:border-brand-green focus:border-brand-green focus:outline-none flex-1 min-w-0 px-0.5"
          data-testid={testId}
        />
      ) : (
        <span className="text-xs text-brand-text">{eq.food_name}</span>
      )}
      {eq.measure_display && eq.quantity && <span className="text-[9px] text-brand-muted">({eq.quantity}g)</span>}
    </div>
  );
}

function calcFoodMacros(item) {
  const qty = parseFloat(item.quantity) || 0;
  const f = qty / 100;
  return {
    energia: Math.round((item.energia_kcal_100||0)*f*10)/10,
    proteina: Math.round((item.proteina_g_100||0)*f*10)/10,
    hidratos: Math.round((item.hidratos_g_100||0)*f*10)/10,
    gordura: Math.round((item.gordura_g_100||0)*f*10)/10,
    fibra: Math.round((item.fibra_g_100||0)*f*10)/10,
    calcio: Math.round((item.calcio_mg_100||0)*f*10)/10,
    ferro: Math.round((item.ferro_mg_100||0)*f*100)/100,
    sodio: Math.round((item.sodio_mg_100||0)*f*10)/10,
    vit_c: Math.round((item.vit_c_mg_100||0)*f*10)/10,
    vit_d: Math.round((item.vit_d_ug_100||0)*f*100)/100,
    potassio: Math.round((item.potassio_mg_100||0)*f*10)/10,
    magnesio: Math.round((item.magnesio_mg_100||0)*f*10)/10,
    folato: Math.round((item.folato_ug_100||0)*f*10)/10,
  };
}
function calcMealTotals(foods) {
  const t = { energia:0, proteina:0, hidratos:0, gordura:0, fibra:0, calcio:0, ferro:0, sodio:0, vit_c:0, vit_d:0, potassio:0, magnesio:0, folato:0 };
  (foods||[]).forEach(i => { const m=calcFoodMacros(i); t.energia+=m.energia; t.proteina+=m.proteina; t.hidratos+=m.hidratos; t.gordura+=m.gordura; t.fibra+=m.fibra; t.calcio+=m.calcio; t.ferro+=m.ferro; t.sodio+=m.sodio; t.vit_c+=m.vit_c; t.vit_d+=m.vit_d; t.potassio+=m.potassio; t.magnesio+=m.magnesio; t.folato+=m.folato; });
  return { energia:Math.round(t.energia*10)/10, proteina:Math.round(t.proteina*10)/10, hidratos:Math.round(t.hidratos*10)/10, gordura:Math.round(t.gordura*10)/10, fibra:Math.round(t.fibra*10)/10, calcio:Math.round(t.calcio*10)/10, ferro:Math.round(t.ferro*100)/100, sodio:Math.round(t.sodio*10)/10, vit_c:Math.round(t.vit_c*10)/10, vit_d:Math.round(t.vit_d*100)/100, potassio:Math.round(t.potassio*10)/10, magnesio:Math.round(t.magnesio*10)/10, folato:Math.round(t.folato*10)/10 };
}

function MicroBar({ macros }) {
  const items = [
    { l:'Ca (mg)', v:macros.calcio },
    { l:'Fe (mg)', v:macros.ferro },
    { l:'Na (mg)', v:macros.sodio },
    { l:'K (mg)', v:macros.potassio },
    { l:'Mg (mg)', v:macros.magnesio },
    { l:'Vit C (mg)', v:macros.vit_c },
    { l:'Vit D (µg)', v:macros.vit_d },
    { l:'Folato (µg)', v:macros.folato },
  ];
  const hasAny = items.some(x => (x.v || 0) > 0);
  if (!hasAny) return null;
  return (
    <div className="mt-2 grid grid-cols-4 gap-1.5" data-testid="micro-totals">
      {items.map(({ l, v }) => (
        <div key={l} className="flex flex-col items-center px-1.5 py-1 rounded-lg bg-brand-bg/60">
          <span className="text-xs font-medium font-heading text-brand-text">{v}</span>
          <span className="text-[9px] text-brand-muted leading-tight text-center">{l}</span>
        </div>
      ))}
    </div>
  );
}

function MacroBar({ macros }) {
  return (
    <div className="grid grid-cols-5 gap-1.5" data-testid="macro-totals">
      {[
        { l:'kcal', v:macros.energia, icon:Flame, hi:true },
        { l:'prot', v:macros.proteina, icon:Barbell, hi:true },
        { l:'HC', v:macros.hidratos, icon:Grains },
        { l:'gord', v:macros.gordura, icon:Drop },
        { l:'fibra', v:macros.fibra, icon:Leaf },
      ].map(({l,v,icon:I,hi})=>(
        <div key={l} className={`flex flex-col items-center px-1.5 py-1.5 rounded-xl ${hi?'bg-brand-green/10':'bg-brand-bg'}`}>
          <I size={13} weight="duotone" className={hi?'text-brand-green':'text-brand-muted'} />
          <span className={`text-sm font-semibold font-heading ${hi?'text-brand-green':'text-brand-text'}`}>{v}</span>
          <span className="text-[9px] text-brand-muted">{l}</span>
        </div>
      ))}
    </div>
  );
}

// ─── CALCULADORA → PLANO: comparação Real vs Objetivo + DDR micronutrientes ───
const TARGET_MARGIN = 0.10; // ±10% considera-se "no alvo"

// Valores de Dose Diária Recomendada (DDR) de micronutrientes (referência EFSA/DGS adulto),
// com ajuste automático para gravidez e amamentação (pós-parto). Sódio é um LIMITE máximo.
function getMicroDDR(patientType) {
  if (patientType === 'gravida') return { calcio:1000, ferro:27, magnesio:360, potassio:3500, vit_c:105, vit_d:15, folato:600, sodio:2000 };
  if (patientType === 'pos_parto') return { calcio:1000, ferro:16, magnesio:360, potassio:4000, vit_c:155, vit_d:15, folato:500, sodio:2000 };
  return { calcio:1000, ferro:18, magnesio:300, potassio:3500, vit_c:80, vit_d:15, folato:330, sodio:2000 };
}

const MACRO_TARGET_ROWS = [
  { key:'energia', tkey:'energia_kcal', label:'Energia', unit:'kcal' },
  { key:'proteina', tkey:'proteina_g', label:'Proteína', unit:'g' },
  { key:'hidratos', tkey:'hidratos_g', label:'Hidratos', unit:'g' },
  { key:'gordura', tkey:'gordura_g', label:'Gordura', unit:'g' },
  { key:'fibra', tkey:'fibra_g', label:'Fibra', unit:'g' },
];

const MICRO_DDR_DEFS = [
  { key:'calcio', label:'Cálcio', unit:'mg' },
  { key:'ferro', label:'Ferro', unit:'mg' },
  { key:'magnesio', label:'Magnésio', unit:'mg' },
  { key:'potassio', label:'Potássio', unit:'mg' },
  { key:'vit_c', label:'Vitamina C', unit:'mg' },
  { key:'vit_d', label:'Vitamina D', unit:'µg' },
  { key:'folato', label:'Folato', unit:'µg' },
  { key:'sodio', label:'Sódio', unit:'mg', isLimit:true },
];

const STATUS_STYLE = {
  within: { bar:'bg-brand-green', text:'text-brand-green', chip:'bg-brand-mint/20 text-brand-green' },
  below:  { bar:'bg-amber-400', text:'text-amber-600', chip:'bg-amber-100 text-amber-700' },
  above:  { bar:'bg-red-400', text:'text-red-500', chip:'bg-red-100 text-red-600' },
};

function macroTargetStatus(actual, target) {
  if (!target || target <= 0) return null;
  const pct = actual / target;
  if (pct < 1 - TARGET_MARGIN) return 'below';
  if (pct > 1 + TARGET_MARGIN) return 'above';
  return 'within';
}

function microTargetStatus(actual, ddr, isLimit) {
  if (!ddr || ddr <= 0) return null;
  const pct = actual / ddr;
  if (isLimit) return pct > 1 + TARGET_MARGIN ? 'above' : 'within'; // sódio: acima do limite = mau
  if (pct >= 1) return 'within';
  if (pct >= 0.7) return 'below';
  return 'above'; // <70% da DDR = défice acentuado (vermelho)
}

function CompareRow({ label, unit, actual, target, status }) {
  const st = status ? STATUS_STYLE[status] : null;
  const pct = target > 0 ? Math.round((actual / target) * 100) : 0;
  return (
    <div className="flex items-center gap-2.5" data-testid={`compare-row-${label}`}>
      <div className="w-20 shrink-0"><span className="text-[11px] font-medium text-brand-text">{label}</span></div>
      <div className="flex-1 h-2.5 bg-brand-border/60 rounded-full overflow-hidden">
        <div className={`h-full rounded-full transition-all ${st ? st.bar : 'bg-brand-muted'}`} style={{ width: `${Math.min(100, pct)}%` }} />
      </div>
      <div className="w-28 shrink-0 text-right">
        <span className="text-[11px] font-heading font-semibold text-brand-text">{actual}</span>
        <span className="text-[10px] text-brand-muted"> / {Math.round(target)} {unit}</span>
      </div>
      <div className="w-12 shrink-0 text-right">
        {st ? <span className={`text-[10px] font-semibold ${st.text}`}>{pct}%</span> : <span className="text-[10px] text-brand-placeholder">-</span>}
      </div>
    </div>
  );
}

function TargetComparisonPanel({ actual, targets, patientType, onSave, saving }) {
  const t = targets || {};
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const hasTargets = ['energia_kcal','proteina_g','hidratos_g','gordura_g','fibra_g'].some(k => Number(t[k]) > 0);
  const ddr = getMicroDDR(patientType);

  const startEdit = () => {
    setForm({
      energia_kcal: t.energia_kcal || '', proteina_g: t.proteina_g || '', hidratos_g: t.hidratos_g || '',
      gordura_g: t.gordura_g || '', fibra_g: t.fibra_g || '',
    });
    setEditing(true);
  };
  const handleSave = async () => {
    const next = {
      ...t,
      energia_kcal: Number(form.energia_kcal) || 0,
      proteina_g: Number(form.proteina_g) || 0,
      hidratos_g: Number(form.hidratos_g) || 0,
      gordura_g: Number(form.gordura_g) || 0,
      fibra_g: Number(form.fibra_g) || 0,
    };
    await onSave(next);
    setEditing(false);
  };

  return (
    <div className="bg-white border border-brand-gold/30 rounded-3xl p-4 mb-5 shadow-[0_8px_32px_rgba(101,113,97,0.06)]" data-testid="target-comparison-panel">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-heading font-medium text-xs text-brand-muted uppercase tracking-wider flex items-center gap-1.5"><Target size={14} weight="duotone" className="text-brand-gold-hover" /> Real vs Objetivo</h3>
        {!editing && (
          <button onClick={startEdit} className="text-[11px] text-brand-green flex items-center gap-1 hover:underline" data-testid="edit-targets-btn">
            <PencilSimple size={12} /> {hasTargets ? 'Editar objetivos' : 'Definir objetivos'}
          </button>
        )}
      </div>

      {editing ? (
        <div data-testid="targets-edit-form">
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-3">
            {MACRO_TARGET_ROWS.map(r => (
              <div key={r.tkey}>
                <Label className="text-[10px] text-brand-muted mb-0.5 block">{r.label} ({r.unit})</Label>
                <Input type="number" step="any" value={form[r.tkey]} onChange={e => setForm({ ...form, [r.tkey]: e.target.value })} className="rounded-lg border-brand-border text-sm h-8" data-testid={`target-input-${r.tkey}`} />
              </div>
            ))}
          </div>
          <div className="flex gap-2 justify-end">
            <button onClick={() => setEditing(false)} className="text-xs text-brand-muted border border-brand-border rounded-full px-3 py-1.5 hover:bg-brand-bg" data-testid="cancel-targets-btn">Cancelar</button>
            <button onClick={handleSave} disabled={saving} className="text-xs bg-brand-green text-white rounded-full px-4 py-1.5 font-medium disabled:opacity-50 flex items-center gap-1.5" data-testid="save-targets-btn"><FloppyDisk size={13} /> {saving ? 'A guardar...' : 'Guardar objetivos'}</button>
          </div>
        </div>
      ) : !hasTargets ? (
        <p className="text-xs text-brand-muted py-2" data-testid="no-targets-msg">Sem objetivos definidos. Importa da calculadora de necessidades ou define-os aqui para acompanhar o progresso do plano.</p>
      ) : (
        <>
          <div className="space-y-2">
            {MACRO_TARGET_ROWS.map(r => {
              const target = Number(t[r.tkey]) || 0;
              if (target <= 0) return null;
              return <CompareRow key={r.tkey} label={r.label} unit={r.unit} actual={actual[r.key] || 0} target={target} status={macroTargetStatus(actual[r.key] || 0, target)} />;
            })}
          </div>
          <div className="mt-4 pt-3 border-t border-brand-border/50">
            <p className="text-[10px] text-brand-muted uppercase tracking-wider mb-2">Micronutrientes vs DDR{patientType === 'gravida' ? ' (gravidez)' : patientType === 'pos_parto' ? ' (amamentação)' : ''}</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-2" data-testid="micro-ddr-grid">
              {MICRO_DDR_DEFS.map(m => (
                <CompareRow key={m.key} label={m.label} unit={m.unit} actual={actual[m.key] || 0} target={ddr[m.key]} status={microTargetStatus(actual[m.key] || 0, ddr[m.key], m.isLimit)} />
              ))}
            </div>
            <p className="text-[9px] text-brand-placeholder mt-2">DDR: dose diária recomendada (EFSA/DGS). Sódio é um limite máximo (verde = abaixo do limite).</p>
          </div>
        </>
      )}
    </div>
  );
}

// Estado do plano face ao objetivo de kcal (usado nos cartões da lista)
function planKcalTargetStatus(plan) {
  const target = Number(plan?.nutritional_targets?.energia_kcal || 0);
  if (!target) return null;
  let kcal = 0;
  const meals = plan?.meals || {};
  Object.keys(meals).forEach(k => {
    if (k.endsWith('_notes') || !Array.isArray(meals[k])) return;
    meals[k].forEach(it => { kcal += (Number(it.energia_kcal_100) || 0) * (Number(it.quantity) || 0) / 100; });
  });
  kcal = Math.round(kcal);
  if (kcal <= 0) return { status: 'empty', kcal, target };
  const pct = kcal / target;
  if (pct < 1 - TARGET_MARGIN) return { status: 'below', kcal, target };
  if (pct > 1 + TARGET_MARGIN) return { status: 'above', kcal, target };
  return { status: 'within', kcal, target };
}

function PlanTargetBadge({ plan }) {
  const s = planKcalTargetStatus(plan);
  if (!s) return null;
  const map = {
    within: { cls: 'bg-brand-mint/20 text-brand-green', icon: CheckCircle, label: 'No objetivo' },
    below:  { cls: 'bg-amber-100 text-amber-700', icon: ArrowDown, label: 'Abaixo do objetivo' },
    above:  { cls: 'bg-red-100 text-red-600', icon: ArrowUp, label: 'Acima do objetivo' },
    empty:  { cls: 'bg-brand-bg text-brand-muted', icon: Target, label: 'Plano vazio' },
  };
  const m = map[s.status];
  const I = m.icon;
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] font-medium px-2 py-1 rounded-full ${m.cls}`} data-testid={`plan-target-status-${plan.id}`} title={`${s.kcal} kcal de ${Math.round(s.target)} kcal`}>
      <I size={11} weight="bold" /> {m.label} · {s.kcal}/{Math.round(s.target)} kcal
    </span>
  );
}

// ─── PLAN DETAIL ───
export function PlanDetail({ plan, onBack, onUpdate, isNutri, defaultMealTypes }) {
  const { user } = useAuth();
  const confirmDelete = useConfirmDelete();
  const isClient = user?.role === 'cliente';
  const [sp, setSp] = useState(plan);
  const [patientType, setPatientType] = useState(plan?.patient_type || '');
  const [savingTargets, setSavingTargets] = useState(false);
  const [activeVersionId, setActiveVersionId] = useState(plan?.active_version_id || plan?.default_version_id || '');
  const [showVersionsModal, setShowVersionsModal] = useState(false);
  const [showCompareModal, setShowCompareModal] = useState(false);
  const [showCreateVersion, setShowCreateVersion] = useState(false);
  const [addingTo, setAddingTo] = useState(null);
  const [editingPlanName, setEditingPlanName] = useState(false);
  const [planNameVal, setPlanNameVal] = useState(plan.name || '');
  const [dragSource, setDragSource] = useState(null); // { mk, idx }
  const [dragOverTarget, setDragOverTarget] = useState(null); // { mk, section } for drop target
  const [foods, setFoods] = useState([]);
  const [searchFood, setSearchFood] = useState('');
  const [qty, setQty] = useState('100');
  const [unit, setUnit] = useState('g');
  const [editQty, setEditQty] = useState(null);
  const [newQty, setNewQty] = useState('');
  const [editName, setEditName] = useState(null);
  const [newName, setNewName] = useState('');
  const [editNotes, setEditNotes] = useState(null);
  const [noteText, setNoteText] = useState('');
  const [showAddMeal, setShowAddMeal] = useState(false);
  const [newMealLabel, setNewMealLabel] = useState('');
  const [newMealTime, setNewMealTime] = useState('');
  const [editMealCfg, setEditMealCfg] = useState(null);
  const [editMealLabel, setEditMealLabel] = useState('');
  const [editMealTime, setEditMealTime] = useState('');
  const [showEquiv, setShowEquiv] = useState(null); // {mk, idx}
  const [selectedFood, setSelectedFood] = useState(null); // food selected for adding, shows measures
  const [foodMeasures, setFoodMeasures] = useState([]);
  const [equivGroups, setEquivGroups] = useState([]);
  const [addingSection, setAddingSection] = useState('');
  const [showNewSection, setShowNewSection] = useState(null);
  const [newSectionName, setNewSectionName] = useState('');
  const DEFAULT_SECTIONS = ['Sopa', 'Prato', 'Sobremesa', 'Bebida'];

  const fetchFoods = useCallback(async () => {
    try {
      const [fRes, eRes] = await Promise.all([
        axios.get(`${API}/api/foods?search=${searchFood}&limit=20`),
        axios.get(`${API}/api/equivalent-groups`),
      ]);
      setFoods(fRes.data);
      setEquivGroups(eRes.data || []);
    } catch{}
  }, [searchFood]);
  useEffect(()=>{ if(addingTo) fetchFoods(); }, [fetchFoods, addingTo]);

  const mealConfig = sp.meal_config || {};
  const getMealCfg = (k) => {
    const cfg = mealConfig[k] || {};
    const def = defaultMealTypes.find(m=>m.key===k) || {};
    return { label: cfg.label||def.label||k.replace(/_/g,' ').replace(/\b\w/g,l=>l.toUpperCase()), time: cfg.time||def.time||'', order: cfg.order ?? def.order ?? 99 };
  };

  const allMealKeys = useMemo(()=>{
    const keys = Object.keys(sp.meals||{}).filter(k=>!k.endsWith('_notes')&&Array.isArray(sp.meals[k]));
    keys.sort((a,b)=>{
      const ca = getMealCfg(a), cb = getMealCfg(b);
      // Use manual order if set, otherwise sort by time
      if (ca.order !== undefined && ca.order !== 99 && cb.order !== undefined && cb.order !== 99) {
        return (ca.order || 0) - (cb.order || 0);
      }
      const ta = ca.time || '99:99', tb = cb.time || '99:99';
      return ta.localeCompare(tb);
    });
    return keys;
  }, [sp, mealConfig, defaultMealTypes]);

  const moveMeal = async (fromIdx, toIdx) => {
    if (fromIdx === toIdx) return;
    const keys = [...allMealKeys];
    const [moved] = keys.splice(fromIdx, 1);
    keys.splice(toIdx, 0, moved);
    const mc = { ...mealConfig };
    keys.forEach((k, i) => {
      mc[k] = { ...(mc[k] || {}), order: i };
    });
    await savePlan({ meal_config: mc });
    toast.success('Ordem atualizada');
  };

  const dailyTotals = useMemo(()=>{
    const t={energia:0,proteina:0,hidratos:0,gordura:0,fibra:0,calcio:0,ferro:0,sodio:0,vit_c:0,vit_d:0,potassio:0,magnesio:0,folato:0};
    allMealKeys.forEach(k=>{const m=calcMealTotals(sp.meals?.[k]||[]); t.energia+=m.energia;t.proteina+=m.proteina;t.hidratos+=m.hidratos;t.gordura+=m.gordura;t.fibra+=m.fibra;t.calcio+=m.calcio;t.ferro+=m.ferro;t.sodio+=m.sodio;t.vit_c+=m.vit_c;t.vit_d+=m.vit_d;t.potassio+=m.potassio;t.magnesio+=m.magnesio;t.folato+=m.folato;});
    return {energia:Math.round(t.energia*10)/10,proteina:Math.round(t.proteina*10)/10,hidratos:Math.round(t.hidratos*10)/10,gordura:Math.round(t.gordura*10)/10,fibra:Math.round(t.fibra*10)/10,calcio:Math.round(t.calcio*10)/10,ferro:Math.round(t.ferro*100)/100,sodio:Math.round(t.sodio*10)/10,vit_c:Math.round(t.vit_c*10)/10,vit_d:Math.round(t.vit_d*100)/100,potassio:Math.round(t.potassio*10)/10,magnesio:Math.round(t.magnesio*10)/10,folato:Math.round(t.folato*10)/10};
  },[sp,allMealKeys]);

  const deepCopy=()=>{const m={};for(const[k,v]of Object.entries(sp.meals||{})){m[k]=Array.isArray(v)?v.map(i=>({...i,equivalents:i.equivalents?[...i.equivalents.map(e=>({...e}))]:undefined})):v;}return m;};
  const verQ = (vid) => (vid ? `?version_id=${encodeURIComponent(vid)}` : '');
  const savePlan=async(updates)=>{try{const{data}=await axios.put(`${API}/api/meal-plans/${sp.id}${verQ(activeVersionId)}`,updates);setSp(data);if(data?.active_version_id)setActiveVersionId(data.active_version_id);if(onUpdate)onUpdate();}catch{toast.error('Erro ao guardar');}};

  // Fetch patient type to adapt micronutrient DDR (gravidez/amamentação) — nutri only
  useEffect(() => {
    if (isClient || !sp?.client_id) return;
    (async () => {
      try { const { data } = await axios.get(`${API}/api/patients/${sp.client_id}`); setPatientType(data?.patient_type || ''); } catch {}
    })();
  }, [sp?.client_id, isClient]);

  const saveTargets = async (next) => {
    setSavingTargets(true);
    try {
      await savePlan({ nutritional_targets: next });
      toast.success('Objetivos atualizados');
    } finally { setSavingTargets(false); }
  };

  // Switch version (admin: just changes which version we're editing; client: persists today's pick)
  const switchVersion = async (vid) => {
    if (!vid || vid === activeVersionId) return;
    try {
      if (isClient) {
        await axios.put(`${API}/api/meal-plans/${sp.id}/active-version`, { version_id: vid });
      }
      const { data } = await axios.get(`${API}/api/meal-plans?client_id=${sp.client_id}&version_id=${encodeURIComponent(vid)}`);
      const fresh = (data || []).find(p => p.id === sp.id);
      if (fresh) setSp(fresh);
      setActiveVersionId(vid);
      if (isClient) toast.success('Versão activa para hoje');
    } catch {
      toast.error('Erro ao mudar de versão');
    }
  };

  // On mount: client fetches today's active version
  useEffect(() => {
    if (!isClient || !sp?.id) return;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/meal-plans/${sp.id}/active-version`);
        if (data?.active_version_id && data.active_version_id !== activeVersionId) {
          const r = await axios.get(`${API}/api/meal-plans?client_id=${user?.user_id}&version_id=${encodeURIComponent(data.active_version_id)}`);
          const fresh = (r.data || []).find(p => p.id === sp.id);
          if (fresh) { setSp(fresh); setActiveVersionId(data.active_version_id); }
        }
      } catch {}
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sp?.id, isClient]);

  const handleAddFood=async(food)=>{
    if(!addingTo)return;const meals=deepCopy();if(!meals[addingTo])meals[addingTo]=[];
    meals[addingTo].push({food_id:food.id,food_name:food.name,display_name:food.name,quantity:parseFloat(qty)||100,unit,section:addingSection||'',energia_kcal_100:food.energia_kcal||0,proteina_g_100:food.proteina_g||0,hidratos_g_100:food.hidratos_g||0,gordura_g_100:food.gordura_g||0,fibra_g_100:food.fibra_g||0,calcio_mg_100:food.calcio_mg||0,ferro_mg_100:food.ferro_mg||0,sodio_mg_100:food.sodio_mg||0,vit_c_mg_100:food.vit_c_mg||0,vit_d_ug_100:food.vit_d_ug||0,potassio_mg_100:food.potassio_mg||0,magnesio_mg_100:food.magnesio_mg||0,folato_ug_100:food.folato_ug||0});
    await savePlan({meals});setAddingTo(null);setAddingSection('');toast.success('Alimento adicionado');
  };
  const handleRemove=async(mk,idx)=>{const meals=deepCopy();meals[mk]=meals[mk].filter((_,i)=>i!==idx);await savePlan({meals});};
  const handleMoveFood=async(mk,idx,dir)=>{const meals=deepCopy();const arr=meals[mk];const ni=idx+dir;if(ni<0||ni>=arr.length)return;[arr[idx],arr[ni]]=[arr[ni],arr[idx]];await savePlan({meals});};
  const handleSaveQty=async(mk,idx)=>{const p=parseFloat(newQty);if(isNaN(p)||p<=0){setEditQty(null);return;}const meals=deepCopy();meals[mk][idx]={...meals[mk][idx],quantity:p};await savePlan({meals});setEditQty(null);};
  const handleSaveName=async(mk,idx)=>{const meals=deepCopy();meals[mk][idx]={...meals[mk][idx],display_name:newName};await savePlan({meals});setEditName(null);};
  const handleSaveNotes=async(mk)=>{const meals=deepCopy();meals[`${mk}_notes`]=noteText;await savePlan({meals});setEditNotes(null);toast.success('Notas guardadas');};
  const handleDupMeal=async(fromKey)=>{const nk=`meal_${Date.now()}`;const meals=deepCopy();meals[nk]=(meals[fromKey]||[]).map(i=>({...i}));const mc={...mealConfig};const cfg=getMealCfg(fromKey);mc[nk]={label:cfg.label,time:cfg.time,order:cfg.order+1};await savePlan({meals,meal_config:mc});toast.success('Refeição duplicada');};
  const handleDeleteMeal=async(mk)=>{const label=getMealCfg(mk)?.label||'esta refeição';const ok=await confirmDelete({description:`Vai apagar a refeição "${label}" e todos os seus alimentos.`});if(!ok)return;const meals=deepCopy();delete meals[mk];delete meals[`${mk}_notes`];const mc={...mealConfig};delete mc[mk];await savePlan({meals,meal_config:mc});toast.success('Refeição eliminada');};
  const handleAddMeal=async()=>{if(!newMealLabel.trim())return;const nk=`meal_${Date.now()}`;const meals=deepCopy();meals[nk]=[];const mc={...mealConfig};mc[nk]={label:newMealLabel,time:newMealTime,order:allMealKeys.length};await savePlan({meals,meal_config:mc});setShowAddMeal(false);setNewMealLabel('');setNewMealTime('');toast.success('Refeicao criada');};
  const handleEditMealCfg=async()=>{const mc={...mealConfig};mc[editMealCfg]={...mc[editMealCfg],label:editMealLabel,time:editMealTime};
    // Reset manual order when time changes so auto-sort by time takes over
    if (editMealTime !== (mealConfig[editMealCfg]?.time || '')) {
      Object.keys(mc).forEach(k => { if (mc[k]) delete mc[k].order; });
    }
    await savePlan({meal_config:mc});setEditMealCfg(null);};
  const handleSaveEquiv=async(mk,idx,equivs)=>{
    const meals=deepCopy();meals[mk][idx]={...meals[mk][idx],equivalents:equivs};await savePlan({meals});setShowEquiv(null);toast.success('Equivalentes guardados');
    // Offer to save to library
    const refFood = meals[mk][idx];
    setEquivLibData({ referenceFood: { food_id: refFood.food_id, food_name: refFood.display_name || refFood.food_name, quantity: refFood.quantity, unit: refFood.unit }, equivalents: equivs });
    setEquivLibName(`${refFood.display_name || refFood.food_name} (${refFood.quantity}${refFood.unit})`);
    setShowSaveEquivLib(true);
  };
  const handleToggleStatus=async()=>{const ns=sp.status==='ativo'?'rascunho':'ativo';try{const{data}=await axios.put(`${API}/api/meal-plans/${sp.id}`,{status:ns});setSp(data);if(onUpdate)onUpdate();toast.success(`Plano ${ns==='ativo'?'ativado':'desativado'}`);}catch{toast.error('Erro');}};

  // Rename plan
  const handleRenamePlan = async (name) => {
    if (!name.trim()) return;
    try { const { data } = await axios.put(`${API}/api/meal-plans/${sp.id}`, { name: name.trim() }); setSp(data); if (onUpdate) onUpdate(); } catch { toast.error('Erro ao renomear'); }
  };

  // Drag-and-drop food between meals
  const handleDragStart = (mk, idx) => { setDragSource({ mk, idx }); };
  const handleDragOverSection = (e, targetMk, targetSection) => { e.preventDefault(); e.stopPropagation(); setDragOverTarget({ mk: targetMk, section: targetSection }); };
  const handleDragOverMeal = (e, targetMk) => { e.preventDefault(); setDragOverTarget(prev => prev?.mk === targetMk ? prev : { mk: targetMk, section: null }); };
  const handleDragLeave = (e) => { if (!e.currentTarget.contains(e.relatedTarget)) setDragOverTarget(null); };
  const handleDrop = async (e, targetMk, targetSection) => {
    e.preventDefault(); e.stopPropagation(); setDragOverTarget(null);
    if (!dragSource) return;
    const srcMk = dragSource.mk;
    const srcIdx = dragSource.idx;
    const meals = deepCopy();
    const srcArr = meals[srcMk] || [];
    if (srcIdx >= srcArr.length) { setDragSource(null); return; }
    const movedItem = srcArr[srcIdx];
    const srcSection = movedItem.section || '';
    const destSection = targetSection ?? '';
    if (srcMk === targetMk && srcSection === destSection) { setDragSource(null); return; }
    if (srcMk === targetMk) {
      meals[srcMk][srcIdx] = { ...movedItem, section: destSection };
    } else {
      srcArr.splice(srcIdx, 1);
      meals[srcMk] = srcArr;
      if (!meals[targetMk]) meals[targetMk] = [];
      meals[targetMk].push({ ...movedItem, section: destSection });
    }
    try {
      await savePlan({ meals });
      toast.success('Alimento movido');
    } catch { /* savePlan already shows error toast */ }
    setDragSource(null);
  };  const handlePDF=async(version)=>{try{const vq=activeVersionId?`&version_id=${encodeURIComponent(activeVersionId)}`:'';const r=await axios.get(`${API}/api/meal-plans/${sp.id}/pdf?version=${version}${vq}`,{responseType:'blob'});const u=window.URL.createObjectURL(new Blob([r.data],{type:'application/pdf'}));const a=document.createElement('a');a.href=u;a.download=`plano_${sp.name.replace(/ /g,'_')}_${version}.pdf`;a.click();toast.success('PDF gerado');}catch{toast.error('Erro ao gerar PDF');}};
  const [showSaveModel, setShowSaveModel] = useState(false);
  const [modelName, setModelName] = useState('');
  const handleSaveAsModel = async () => {
    try {
      await axios.post(`${API}/api/plan-templates`, { source_plan_id: sp.id, name: modelName || sp.name });
      toast.success('Plano guardado como modelo');
      setShowSaveModel(false);
    } catch { toast.error('Erro ao guardar modelo'); }
  };
  const [showSaveEquivLib, setShowSaveEquivLib] = useState(false);
  const [equivLibName, setEquivLibName] = useState('');
  const [equivLibData, setEquivLibData] = useState(null);
  const handleSaveEquivToLibrary = async () => {
    if (!equivLibData) return;
    try {
      await axios.post(`${API}/api/equivalent-groups`, {
        name: equivLibName || 'Grupo de equivalentes',
        reference_food: equivLibData.referenceFood,
        foods: equivLibData.equivalents.map(eq => ({ food_id: eq.food_id || '', food_name: eq.food_name, equivalent_portion: eq.quantity || '', unit: eq.unit || 'g', measure_display: eq.measure_display || null })),
      });
      toast.success('Equivalentes guardados na biblioteca');
      setShowSaveEquivLib(false);
    } catch { toast.error('Erro'); }
  };
  return (
    <div className="fade-in" data-testid="plano-detail">
      {/* Header */}
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div>
          <button onClick={onBack} className="text-sm text-brand-green mb-1 hover:underline" data-testid="back-to-plans">&larr; Voltar</button>
          {editingPlanName ? (
            <input
              value={planNameVal} onChange={e => setPlanNameVal(e.target.value)}
              onBlur={() => { handleRenamePlan(planNameVal); setEditingPlanName(false); }}
              onKeyDown={e => { if (e.key === 'Enter') { handleRenamePlan(planNameVal); setEditingPlanName(false); } if (e.key === 'Escape') { setPlanNameVal(sp.name); setEditingPlanName(false); } }}
              className="font-heading font-medium text-xl text-brand-text border-b-2 border-brand-green bg-transparent outline-none w-full"
              autoFocus data-testid="edit-plan-name-input"
            />
          ) : (
            <h2 className="font-heading font-medium text-xl text-brand-text group cursor-pointer" onClick={() => { if (isNutri) { setPlanNameVal(sp.name); setEditingPlanName(true); } }} data-testid="plan-name-display">
              {sp.name} {isNutri && <PencilSimple size={13} className="inline text-brand-placeholder group-hover:text-brand-green ml-1" />}
            </h2>
          )}
          <span className={`inline-block mt-1 text-xs font-medium px-2.5 py-1 rounded-full ${sp.status==='ativo'?'bg-brand-mint/20 text-brand-green':'bg-brand-bg text-brand-muted'}`}>{sp.status==='ativo'?'Ativo':'Rascunho'}</span>
        </div>
        {isNutri && (
          <div className="flex gap-2 flex-wrap">
            <ShareButton resources={{ plan: sp.id }} label={sp.name || 'Plano alimentar'} />
            <button onClick={()=>handlePDF('client')} className="border border-brand-border text-brand-green hover:bg-brand-green/5 rounded-full px-3 py-1.5 text-xs font-medium flex items-center gap-1" data-testid="pdf-client-btn"><FilePdf size={14} /> PDF Cliente</button>
            <button onClick={()=>handlePDF('nutri')} className="border border-brand-border text-brand-muted hover:bg-brand-bg rounded-full px-3 py-1.5 text-xs font-medium flex items-center gap-1" data-testid="pdf-nutri-btn"><FilePdf size={14} /> PDF Nutri</button>
            <button onClick={handleToggleStatus} className="border border-brand-border text-brand-green hover:bg-brand-green/5 rounded-full px-3 py-1.5 text-xs font-medium" data-testid="toggle-plan-status">{sp.status==='ativo'?'Desativar':'Ativar'}</button>
            <button onClick={() => { setModelName(sp.name); setShowSaveModel(true); }} className="border border-brand-border text-brand-muted hover:bg-brand-bg rounded-full px-3 py-1.5 text-xs font-medium" data-testid="save-as-model-btn">Guardar como modelo</button>
          </div>
        )}
        {isClient && <ShareButton resources={{ plan: sp.id }} label={sp.name || 'Plano alimentar'} />}
      </div>

      {/* Versions selector + actions */}
      <PlanVersionsBar
        plan={sp}
        activeVersionId={activeVersionId}
        isNutri={isNutri}
        isClient={isClient}
        onSwitch={switchVersion}
        onOpenHistory={() => setShowVersionsModal(true)}
        onOpenCompare={() => setShowCompareModal(true)}
        onOpenCreate={() => setShowCreateVersion(true)}
      />

      {/* Item 8 Fase 5 — Histórico de versões arquivadas */}
      <ArchivedVersionsPanel plan={sp} isNutri={isNutri} isClient={isClient} onPlanChanged={(p) => setSp(p)} />

      {/* Daily totals - only for nutri */}
      {!isClient && (
        <div className="bg-white border border-brand-green/20 rounded-3xl p-4 mb-5 shadow-[0_8px_32px_rgba(101,113,97,0.06)]" data-testid="daily-totals-card">
          <h3 className="font-heading font-medium text-xs text-brand-muted uppercase tracking-wider mb-3">Total Diário</h3>
          <MacroBar macros={dailyTotals} />
          <MicroBar macros={dailyTotals} />
        </div>
      )}

      {/* Calculadora → Plano: comparação Real vs Objetivo (nutri only) */}
      {!isClient && (
        <TargetComparisonPanel actual={dailyTotals} targets={sp.nutritional_targets} patientType={patientType} onSave={saveTargets} saving={savingTargets} />
      )}

      <GeneralNotesCard plan={sp} isNutri={isNutri} onSave={savePlan} />

      {/* Add meal button */}
      {isNutri && (
        <div className="flex justify-end mb-3">
          <button onClick={()=>setShowAddMeal(true)} className="text-sm text-brand-green flex items-center gap-1 hover:underline" data-testid="add-meal-btn"><Plus size={14} /> Nova refeição</button>
        </div>
      )}

      {/* Meals */}
      <div className="space-y-4">
        {allMealKeys.map((mk, midx)=>{
          const mealFoods=sp.meals?.[mk]||[];
          const cfg=getMealCfg(mk);
          const mealTotals=calcMealTotals(mealFoods);
          const hasFoods=mealFoods.length>0;
          const notes=sp.meals?.[`${mk}_notes`]||'';
          return (
            <div key={mk} className={`bg-white border rounded-3xl p-4 shadow-[0_8px_32px_rgba(101,113,97,0.04)] transition-all ${dragOverTarget?.mk === mk && !dragOverTarget?.section && dragSource && !(dragSource.mk === mk) ? 'border-brand-green border-2 bg-brand-green/5' : 'border-brand-border'}`} data-testid={`meal-slot-${mk}`}
              onDragOver={e => isNutri && handleDragOverMeal(e, mk)} onDragLeave={handleDragLeave} onDrop={e => isNutri && handleDrop(e, mk, null)}>
              {/* Meal header with time */}
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <h3 className="font-heading font-medium text-sm text-brand-text">{cfg.label}</h3>
                  {cfg.time && <span className="text-[11px] text-brand-muted flex items-center gap-0.5"><Clock size={11}/>{cfg.time}</span>}
                  {isNutri && <button onClick={()=>{setEditMealCfg(mk);setEditMealLabel(cfg.label);setEditMealTime(cfg.time);}} className="text-brand-placeholder hover:text-brand-green"><PencilSimple size={11}/></button>}
                </div>
                {isNutri && (
                  <div className="flex items-center gap-2">
                    {midx > 0 && <button onClick={() => moveMeal(midx, midx - 1)} className="text-brand-placeholder hover:text-brand-green" title="Mover para cima"><CaretUp size={13} /></button>}
                    {midx < allMealKeys.length - 1 && <button onClick={() => moveMeal(midx, midx + 1)} className="text-brand-placeholder hover:text-brand-green" title="Mover para baixo"><CaretDown size={13} /></button>}
                    <button onClick={()=>handleDupMeal(mk)} className="text-brand-placeholder hover:text-brand-green" title="Duplicar"><Copy size={13}/></button>
                    <button onClick={()=>handleDeleteMeal(mk)} className="text-brand-placeholder hover:text-red-500" title="Eliminar refeição"><Trash size={13}/></button>
                    <button onClick={()=>{setEditNotes(mk);setNoteText(notes);}} className="text-brand-placeholder hover:text-brand-green" title="Notas"><NotePencil size={13}/></button>
                    <button onClick={()=>{setAddingTo(mk);setAddingSection('');setSearchFood('');setQty('100');setUnit('g');}} className="text-brand-green hover:text-brand-green-hover text-sm flex items-center gap-1" data-testid={`add-food-to-${mk}`}><Plus size={13}/> Adicionar</button>
                  </div>
                )}
              </div>

              {/* Meal totals - only nutri */}
              {hasFoods && !isClient && (
                <div className="bg-brand-bg rounded-2xl px-3 py-1.5 mb-3">
                  <div className="flex items-center justify-between flex-wrap gap-x-3 text-[11px]">
                    <span className="font-semibold text-brand-green">{mealTotals.energia} kcal</span>
                    <span className="font-medium text-brand-green">P:{mealTotals.proteina}g</span>
                    <span className="text-brand-muted">HC:{mealTotals.hidratos}g</span>
                    <span className="text-brand-muted">G:{mealTotals.gordura}g</span>
                    <span className="text-brand-muted">F:{mealTotals.fibra}g</span>
                  </div>
                </div>
              )}

              {/* Aviso: alimentos sem secção (Sopa/Prato/Sobremesa) numa refeição que já tem
                  outros alimentos COM secção definida, lembra a nutricionista de acabar de
                  organizar antes de guardar, para o PDF e a app da cliente mostrarem sempre
                  a estrutura completa por subgrupos. */}
              {isNutri && hasFoods && (() => {
                const withoutSection = mealFoods.filter(f => !f.section).length;
                const withSection = mealFoods.length - withoutSection;
                if (withoutSection === 0 || withSection === 0) return null;
                return (
                  <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-xl px-3 py-1.5 mb-2" data-testid={`meal-section-warning-${mk}`}>
                    <Warning size={13} className="text-amber-600 flex-shrink-0" weight="fill" />
                    <p className="text-[11px] text-amber-700">{withoutSection} alimento{withoutSection > 1 ? 's' : ''} sem secção (Sopa/Prato/Sobremesa) nesta refeição</p>
                  </div>
                );
              })()}

              {/* Food items - grouped by section */}
              {hasFoods ? (
                <div className="space-y-1">
                  {(() => {
                    // Group by section
                    const sections = {};
                    mealFoods.forEach((item, idx) => {
                      const sec = item.section || '';
                      if (!sections[sec]) sections[sec] = [];
                      sections[sec].push({ item, idx });
                    });
                    const sectionKeys = Object.keys(sections);
                    const hasAnySections = sectionKeys.some(s => s !== '');
                    return sectionKeys.map(sec => {
                      const isSectionDropTarget = dragSource && dragOverTarget?.mk === mk && dragOverTarget?.section === sec;
                      return (
                      <div key={sec || '__none'}
                        onDragOver={e => isNutri && sec && handleDragOverSection(e, mk, sec)}
                        onDrop={e => isNutri && sec && handleDrop(e, mk, sec)}>
                        {(hasAnySections && sec) && (
                          <div className={`flex items-center gap-2 mt-2 mb-1 rounded-lg px-1 py-0.5 transition-all ${isSectionDropTarget ? 'bg-brand-green/10 ring-1 ring-brand-green/40' : ''}`}>
                            <p className="text-[10px] font-semibold text-brand-green uppercase tracking-wider">{sec}</p>
                            <div className="flex-1 h-px bg-brand-border/40" />
                            {isSectionDropTarget && <span className="text-[9px] text-brand-green">Largar aqui</span>}
                          </div>
                        )}
                        <div className="space-y-2">
                          {sections[sec].map(({ item, idx }) => {
                    const im=calcFoodMacros(item);
                    const isEQ=editQty?.mk===mk&&editQty?.idx===idx;
                    const isEN=editName?.mk===mk&&editName?.idx===idx;
                    return (
                      <div key={idx} className={`p-2.5 bg-brand-bg/50 rounded-xl border border-brand-border/30 ${dragSource?.mk === mk && dragSource?.idx === idx ? 'opacity-40' : ''}`} data-testid={`food-${mk}-${idx}`}
                        draggable={isNutri} onDragStart={() => isNutri && handleDragStart(mk, idx)} onDragEnd={() => setDragSource(null)}>
                        <div className="flex items-start gap-2">
                          {isNutri && <div className="cursor-grab active:cursor-grabbing text-brand-placeholder hover:text-brand-green pt-1 flex-shrink-0" title="Arrastar para outra refeição"><DotsSixVertical size={14} /></div>}
                          <div className="flex-1 min-w-0">
                            {/* Display name */}
                            {isEN ? (
                              <div className="flex items-center gap-1 mb-1">
                                <input value={newName} onChange={e=>setNewName(e.target.value)} onKeyDown={e=>{if(e.key==='Enter')handleSaveName(mk,idx);if(e.key==='Escape')setEditName(null);}} className="flex-1 px-1.5 py-0.5 text-sm border border-brand-green rounded-lg bg-white" autoFocus/>
                                <button onClick={()=>handleSaveName(mk,idx)} className="text-[10px] text-brand-green font-medium">OK</button>
                              </div>
                            ) : (
                              <p className="text-sm font-medium text-brand-text">
                                {item.display_name||item.food_name}
                                {isNutri && <button onClick={()=>{setEditName({mk,idx});setNewName(item.display_name||item.food_name);}} className="ml-1 text-brand-placeholder hover:text-brand-green inline"><PencilSimple size={10}/></button>}
                              </p>
                            )}
                            {/* Quantity */}
                            {isEQ ? (
                              <div className="flex items-center gap-1 mt-0.5">
                                <input type="number" value={newQty} onChange={e=>setNewQty(e.target.value)} onKeyDown={e=>{if(e.key==='Enter')handleSaveQty(mk,idx);if(e.key==='Escape')setEditQty(null);}} className="w-16 px-1.5 py-0.5 text-xs border border-brand-green rounded-lg bg-white" autoFocus/>
                                <span className="text-[11px] text-brand-muted">{item.unit}</span>
                                <button onClick={()=>handleSaveQty(mk,idx)} className="text-[10px] text-brand-green font-medium">OK</button>
                              </div>
                            ) : (
                              <button onClick={()=>{if(isNutri){setEditQty({mk,idx});setNewQty(String(item.quantity));}}} className={`text-xs text-brand-muted mt-0.5 ${isNutri?'hover:text-brand-green cursor-pointer':''}`}>
                                {item.quantity} {item.unit}{isNutri && <PencilSimple size={9} className="inline ml-0.5"/>}
                              </button>
                            )}
                            {/* Per-food macros - only nutri */}
                            {!isClient && (
                              <div className="flex gap-2 mt-1 text-[10px] text-brand-muted">
                                <span className="font-medium text-brand-text">{im.energia} kcal</span>
                                <span>P:{im.proteina}g</span><span>HC:{im.hidratos}g</span><span>G:{im.gordura}g</span>
                              </div>
                            )}
                            {/* Equivalents display - editable names */}
                            {item.equivalents?.length>0 && (
                              <div className="mt-2 bg-brand-mint/8 border border-brand-mint/20 rounded-xl px-3 py-2">
                                <p className="text-[10px] text-brand-green font-semibold uppercase tracking-wider mb-1.5 flex items-center gap-1"><Swap size={10} weight="duotone"/>Alternativas</p>
                                <div className="space-y-1">
                                  {item.equivalents.map((eq,ei)=>(
                                    <EquivNameField key={ei} eq={eq} isNutri={isNutri} onSave={(newName) => {
                                      const meals = deepCopy();
                                      meals[mk][idx].equivalents[ei] = { ...meals[mk][idx].equivalents[ei], food_name: newName };
                                      savePlan({ meals });
                                    }} testId={`equiv-name-${mk}-${idx}-${ei}`} />
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                          {isNutri && (
                            <div className="flex flex-col gap-1 items-center">
                              {idx > 0 && <button onClick={()=>handleMoveFood(mk,idx,-1)} className="text-brand-placeholder hover:text-brand-green" title="Mover para cima"><CaretUp size={11}/></button>}
                              {idx < (sp.meals[mk]||[]).length-1 && <button onClick={()=>handleMoveFood(mk,idx,1)} className="text-brand-placeholder hover:text-brand-green" title="Mover para baixo"><CaretDown size={11}/></button>}
                              <button onClick={()=>{setShowEquiv({mk,idx});}} className="text-brand-placeholder hover:text-brand-green" title="Equivalentes"><Swap size={13}/></button>
                              <button onClick={()=>handleRemove(mk,idx)} className="text-brand-placeholder hover:text-red-500"><Trash size={13}/></button>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                        </div>
                      </div>
                    );
                  })
                  })()}
                  {/* Add section button */}
                  {isNutri && (
                    <div className="mt-2">
                      {showNewSection === mk ? (
                        <div className="flex gap-1.5 items-center">
                          <Input value={newSectionName} onChange={e => setNewSectionName(e.target.value)} placeholder="Nome da secção (ex: Sopa)" className="flex-1 rounded-lg border-brand-border text-xs h-7" autoFocus onKeyDown={e => { if (e.key === 'Enter' && newSectionName.trim()) { setAddingTo(mk); setAddingSection(newSectionName.trim()); setSearchFood(''); setQty('100'); setUnit('g'); setShowNewSection(null); setNewSectionName(''); }}} />
                          <button onClick={() => { if (newSectionName.trim()) { setAddingTo(mk); setAddingSection(newSectionName.trim()); setSearchFood(''); setQty('100'); setUnit('g'); setShowNewSection(null); setNewSectionName(''); }}} className="text-[10px] text-brand-green font-medium">OK</button>
                          <button onClick={() => { setShowNewSection(null); setNewSectionName(''); }} className="text-[10px] text-brand-muted">x</button>
                        </div>
                      ) : (
                        <div className="flex gap-2">
                          {DEFAULT_SECTIONS.filter(s => !mealFoods.some(f => f.section === s)).slice(0, 3).map(s => (
                            <button key={s} onClick={() => { setAddingTo(mk); setAddingSection(s); setSearchFood(''); setQty('100'); setUnit('g'); }} className="text-[9px] text-brand-muted hover:text-brand-green border border-dashed border-brand-border/40 rounded-lg px-2 py-1 hover:border-brand-green/30 transition-colors">+ {s}</button>
                          ))}
                          <button onClick={() => { setShowNewSection(mk); setNewSectionName(''); }} className="text-[9px] text-brand-green hover:underline">+ Secção</button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <div>
                  <p className="text-xs text-brand-placeholder mb-2">Sem alimentos</p>
                  {/* Section selector also available when meal is empty (so first food can be added to a section) */}
                  {isNutri && (
                    <div>
                      {showNewSection === mk ? (
                        <div className="flex gap-1.5 items-center">
                          <Input value={newSectionName} onChange={e => setNewSectionName(e.target.value)} placeholder="Nome da secção (ex: Sopa)" className="flex-1 rounded-lg border-brand-border text-xs h-7" autoFocus onKeyDown={e => { if (e.key === 'Enter' && newSectionName.trim()) { setAddingTo(mk); setAddingSection(newSectionName.trim()); setSearchFood(''); setQty('100'); setUnit('g'); setShowNewSection(null); setNewSectionName(''); }}} />
                          <button onClick={() => { if (newSectionName.trim()) { setAddingTo(mk); setAddingSection(newSectionName.trim()); setSearchFood(''); setQty('100'); setUnit('g'); setShowNewSection(null); setNewSectionName(''); }}} className="text-[10px] text-brand-green font-medium">OK</button>
                          <button onClick={() => { setShowNewSection(null); setNewSectionName(''); }} className="text-[10px] text-brand-muted">x</button>
                        </div>
                      ) : (
                        <div className="flex gap-2 flex-wrap">
                          {DEFAULT_SECTIONS.slice(0, 3).map(s => (
                            <button key={s} onClick={() => { setAddingTo(mk); setAddingSection(s); setSearchFood(''); setQty('100'); setUnit('g'); }} className="text-[9px] text-brand-muted hover:text-brand-green border border-dashed border-brand-border/40 rounded-lg px-2 py-1 hover:border-brand-green/30 transition-colors" data-testid={`add-empty-section-${mk}-${s}`}>+ {s}</button>
                          ))}
                          <button onClick={() => { setShowNewSection(mk); setNewSectionName(''); }} className="text-[9px] text-brand-green hover:underline">+ Secção</button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Meal notes */}
              {notes && !editNotes && (
                <div className="mt-3 pt-2 border-t border-brand-border/30">
                  {/\[\[img:/.test(notes) ? <RichNote text={notes} className="text-xs text-brand-muted" /> : <Md as="p" className="text-xs text-brand-muted">{notes}</Md>}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* ═══ DIALOGS ═══ */}

      {/* Add meal */}
      <Dialog open={showAddMeal} onOpenChange={setShowAddMeal}>
        <DialogContent className="rounded-3xl border-brand-border">
          <DialogHeader><DialogTitle className="font-heading text-brand-text">Nova Refeição</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs text-brand-muted mb-1 block">Nome</Label><Input value={newMealLabel} onChange={e=>setNewMealLabel(e.target.value)} placeholder="Ex: Meio da manha" className="rounded-xl border-brand-border" data-testid="new-meal-label"/></div>
            <div><Label className="text-xs text-brand-muted mb-1 block">Hora</Label><Input type="time" value={newMealTime} onChange={e=>setNewMealTime(e.target.value)} className="rounded-xl border-brand-border" data-testid="new-meal-time"/></div>
            <button onClick={handleAddMeal} className="w-full bg-brand-green text-white rounded-full py-3 font-medium" data-testid="submit-new-meal">Criar refeição</button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit meal config */}
      <Dialog open={!!editMealCfg} onOpenChange={()=>setEditMealCfg(null)}>
        <DialogContent className="rounded-3xl border-brand-border">
          <DialogHeader><DialogTitle className="font-heading text-brand-text">Editar Refeição</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs text-brand-muted mb-1 block">Nome</Label><Input value={editMealLabel} onChange={e=>setEditMealLabel(e.target.value)} className="rounded-xl border-brand-border" data-testid="edit-meal-label"/></div>
            <div><Label className="text-xs text-brand-muted mb-1 block">Hora</Label><Input type="time" value={editMealTime} onChange={e=>setEditMealTime(e.target.value)} className="rounded-xl border-brand-border" data-testid="edit-meal-time"/></div>
            <button onClick={handleEditMealCfg} className="w-full bg-brand-green text-white rounded-full py-3 font-medium" data-testid="save-meal-cfg">Guardar</button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Meal notes */}
      <Dialog open={!!editNotes} onOpenChange={()=>setEditNotes(null)}>
        <DialogContent className="rounded-3xl border-brand-border">
          <DialogHeader><DialogTitle className="font-heading text-brand-text">Notas da Refeição</DialogTitle></DialogHeader>
          <p className="text-[11px] text-brand-muted mb-2">Use **texto** para negrito</p>
          <Textarea value={noteText} onChange={e=>setNoteText(e.target.value)} className="rounded-xl border-brand-border" rows={4} data-testid="meal-notes-input"/>
          <div className="mt-2"><InsertImageButton testId="meal-notes-insert-image" onPick={id=>setNoteText(t=>`${t}${t&&!t.endsWith('\n')?'\n':''}[[img:${id}]]\n`)} /></div>
          <button onClick={()=>handleSaveNotes(editNotes)} className="w-full bg-brand-green text-white rounded-full py-3 font-medium mt-2" data-testid="save-meal-notes">Guardar</button>
        </DialogContent>
      </Dialog>

      {/* Equivalents - dynamic Nutrium-style editor */}
      <Dialog open={!!showEquiv} onOpenChange={()=>setShowEquiv(null)}>
        <DialogContent className="rounded-3xl border-brand-border max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading text-brand-text">Equivalentes</DialogTitle></DialogHeader>
          {showEquiv && sp.meals?.[showEquiv.mk]?.[showEquiv.idx] && (
            <DynamicEquivEditor
              referenceFood={sp.meals[showEquiv.mk][showEquiv.idx]}
              currentEquivs={sp.meals[showEquiv.mk][showEquiv.idx].equivalents || []}
              onSave={(equivs) => handleSaveEquiv(showEquiv.mk, showEquiv.idx, equivs)}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Add food dialog - 2 steps: search → select measure + qty */}
      <Dialog open={!!addingTo} onOpenChange={()=>{setAddingTo(null);setSelectedFood(null);setFoodMeasures([]);setAddingSection('');}}>
        <DialogContent className="rounded-3xl border-brand-border max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader><DialogTitle className="font-heading text-brand-text">Adicionar alimento{addingSection ? `: ${addingSection}` : ''}</DialogTitle></DialogHeader>

          {!selectedFood ? (
            <>
              <div className="relative mb-2">
                <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-placeholder"/>
                <Input value={searchFood} onChange={e=>setSearchFood(e.target.value)} placeholder="Procurar alimento..." className="pl-9 rounded-2xl border-brand-border" data-testid="search-food-for-plan"/>
              </div>
              <div className="overflow-y-auto flex-1 space-y-1 max-h-[50vh]">
                {/* Equivalent groups matching search */}
                {equivGroups.filter(g => !searchFood || g.name.toLowerCase().includes(searchFood.toLowerCase()) || (g.reference_food?.food_name || '').toLowerCase().includes(searchFood.toLowerCase())).map(g => (
                  <button key={`eq-${g.id}`} onClick={async () => {
                    // Add reference food with equivalents pre-populated
                    const ref = g.reference_food || {};
                    const refFoodId = ref.food_id;
                    let refFoodData = null;
                    if (refFoodId) {
                      try { const { data: allFoods } = await axios.get(`${API}/api/foods?search=&limit=200`); refFoodData = allFoods.find(f => f.id === refFoodId); } catch {}
                    }
                    if (!addingTo) return;
                    const meals = deepCopy();
                    if (!meals[addingTo]) meals[addingTo] = [];
                    meals[addingTo].push({
                      food_id: refFoodId || '', food_name: ref.food_name || g.name,
                      display_name: ref.food_name || g.name, section: addingSection || '',
                      quantity: ref.quantity || 100, unit: ref.unit || 'g',
                      energia_kcal_100: refFoodData?.energia_kcal || 0,
                      proteina_g_100: refFoodData?.proteina_g || 0,
                      hidratos_g_100: refFoodData?.hidratos_g || 0,
                      gordura_g_100: refFoodData?.gordura_g || 0,
                      fibra_g_100: refFoodData?.fibra_g || 0,
                      equivalents: (g.foods || []).map(f => ({
                        food_id: f.food_id, food_name: f.food_name,
                        quantity: f.equivalent_portion, unit: f.unit || 'g',
                        measure_display: f.measure_display || null,
                      })),
                    });
                    await savePlan({ meals });
                    setAddingTo(null); setSelectedFood(null); setFoodMeasures([]); setAddingSection('');
                    toast.success('Grupo de equivalentes adicionado');
                  }} className="w-full text-left p-3 rounded-2xl hover:bg-purple-50/50 border border-transparent hover:border-purple-200/50 transition-all" data-testid={`select-equiv-${g.id}`}>
                    <div className="flex items-center gap-2">
                      <span className="text-[8px] bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded-full font-medium flex-shrink-0">Equivalentes</span>
                      <p className="text-sm font-medium text-brand-text">{g.name}</p>
                    </div>
                    <p className="text-[10px] text-brand-muted mt-0.5">
                      {g.reference_food?.food_name || ''} ({g.reference_food?.quantity || ''}g) + {(g.foods || []).length} alternativa{(g.foods || []).length !== 1 ? 's' : ''}
                    </p>
                  </button>
                ))}
                {/* Individual foods */}
                {foods.length===0 && equivGroups.length===0?<p className="text-center text-sm text-brand-muted py-4">Sem resultados.</p>:foods.map(food=>(
                  <button key={food.id} onClick={async ()=>{
                    setSelectedFood(food);
                    setQty('100'); setUnit('g');
                    try { const {data}=await axios.get(`${API}/api/foods/${food.id}/measures`); setFoodMeasures(data); } catch { setFoodMeasures([]); }
                  }} className="w-full text-left p-3 rounded-2xl hover:bg-brand-mint/10 border border-transparent hover:border-brand-mint/30 transition-all" data-testid={`select-food-${food.id}`}>
                    <p className="text-sm font-medium text-brand-text">{food.name}</p>
                    <p className="text-[11px] text-brand-muted">{food.energia_kcal} kcal | P:{food.proteina_g}g | HC:{food.hidratos_g}g | G:{food.gordura_g}g</p>
                  </button>
                ))}
              </div>
            </>
          ) : (
            <div className="space-y-4">
              {/* Selected food header */}
              <div className="bg-brand-green/5 border border-brand-green/15 rounded-xl p-3">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium text-brand-green">{selectedFood.name}</p>
                  <button onClick={()=>{setSelectedFood(null);setFoodMeasures([]);}} className="text-[10px] text-brand-muted hover:text-brand-green">Alterar</button>
                </div>
                <p className="text-[10px] text-brand-muted mt-0.5">{selectedFood.energia_kcal} kcal | P:{selectedFood.proteina_g}g | HC:{selectedFood.hidratos_g}g</p>
              </div>

              {/* Measure selection */}
              {foodMeasures.length > 0 && (
                <div>
                  <Label className="text-[10px] text-brand-muted mb-1.5 block">Medida</Label>
                  <div className="flex flex-wrap gap-1.5">
                    <button onClick={()=>{setUnit('g');setQty('100');}} className={`px-3 py-1.5 rounded-full text-xs font-medium ${unit==='g'?'bg-brand-green text-white':'bg-brand-bg text-brand-muted border border-brand-border'}`}>Gramas</button>
                    {foodMeasures.map(m=>(
                      <button key={m.id} onClick={()=>{setUnit(m.measure_name);setQty('1');}} className={`px-3 py-1.5 rounded-full text-xs font-medium ${unit===m.measure_name?'bg-brand-green text-white':'bg-brand-bg text-brand-muted border border-brand-border'}`}>
                        {m.measure_name} <span className="text-[9px] opacity-70">({m.grams_equivalent}g)</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Quantity */}
              <div className="flex gap-3 items-end">
                <div className="flex-1">
                  <Label className="text-[10px] text-brand-muted mb-0.5 block">Quantidade</Label>
                  <Input value={qty} onChange={e=>setQty(e.target.value)} type="number" step="any" className="rounded-2xl border-brand-border text-sm" data-testid="food-plan-qty"/>
                </div>
                <p className="text-sm text-brand-muted pb-2">{unit}</p>
              </div>

              {/* Preview: show calculated grams if using measure */}
              {unit !== 'g' && unit !== 'ml' && (() => {
                const measure = foodMeasures.find(m => m.measure_name === unit);
                if (measure) {
                  const totalGrams = (parseFloat(qty) || 0) * measure.grams_equivalent;
                  const factor = totalGrams / 100;
                  return (
                    <div className="bg-brand-bg rounded-xl p-2.5 text-xs text-brand-muted">
                      <span className="font-medium text-brand-text">{totalGrams}g</span> = {Math.round(selectedFood.energia_kcal * factor)} kcal | P:{Math.round(selectedFood.proteina_g * factor *10)/10}g | HC:{Math.round(selectedFood.hidratos_g * factor *10)/10}g
                    </div>
                  );
                }
                return null;
              })()}

              <button onClick={async ()=>{
                if(!addingTo || !selectedFood) return;
                const measure = foodMeasures.find(m => m.measure_name === unit);
                const gramsQty = measure ? (parseFloat(qty) || 0) * measure.grams_equivalent : parseFloat(qty) || 100;
                const displayName = measure ? `${qty} ${unit} de ${selectedFood.name}` : selectedFood.name;
                const meals = deepCopy();
                if(!meals[addingTo]) meals[addingTo] = [];
                meals[addingTo].push({
                  food_id: selectedFood.id, food_name: selectedFood.name,
                  display_name: displayName, section: addingSection || '',
                  quantity: gramsQty, unit: 'g',
                  measure: measure ? { name: unit, qty: parseFloat(qty), grams: gramsQty } : null,
                  energia_kcal_100: selectedFood.energia_kcal || 0,
                  proteina_g_100: selectedFood.proteina_g || 0,
                  hidratos_g_100: selectedFood.hidratos_g || 0,
                  gordura_g_100: selectedFood.gordura_g || 0,
                  fibra_g_100: selectedFood.fibra_g || 0,
                });
                await savePlan({ meals });
                setAddingTo(null); setSelectedFood(null); setFoodMeasures([]); setAddingSection('');
                toast.success('Alimento adicionado');
              }} className="w-full bg-brand-green text-white rounded-full py-3 font-medium" data-testid="confirm-add-food">Adicionar</button>
            </div>
          )}
        </DialogContent>
      </Dialog>
      {/* Save as model dialog */}
      <Dialog open={showSaveModel} onOpenChange={setShowSaveModel}>
        <DialogContent className="rounded-3xl border-brand-border">
          <DialogHeader><DialogTitle className="font-heading text-brand-text">Guardar como modelo</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-brand-muted">O plano será guardado como modelo reutilizável.</p>
            <div><Label className="text-xs text-brand-muted mb-1 block">Nome do modelo</Label><Input value={modelName} onChange={e => setModelName(e.target.value)} className="rounded-xl border-brand-border" data-testid="model-name-input" /></div>
            <button onClick={handleSaveAsModel} className="w-full bg-brand-green text-white rounded-full py-3 font-medium" data-testid="confirm-save-model">Guardar modelo</button>
          </div>
        </DialogContent>
      </Dialog>
      {/* Save equivalents to library dialog */}
      <Dialog open={showSaveEquivLib} onOpenChange={setShowSaveEquivLib}>
        <DialogContent className="rounded-3xl border-brand-border">
          <DialogHeader><DialogTitle className="font-heading text-brand-text">Guardar na biblioteca</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-brand-muted">Guardar este grupo de equivalentes na biblioteca global?</p>
            <div><Label className="text-xs text-brand-muted mb-1 block">Nome do grupo</Label><Input value={equivLibName} onChange={e => setEquivLibName(e.target.value)} className="rounded-xl border-brand-border" data-testid="equiv-lib-name" /></div>
            <div className="flex gap-2">
              <button onClick={handleSaveEquivToLibrary} className="flex-1 bg-brand-green text-white rounded-full py-2.5 text-sm font-medium" data-testid="confirm-save-equiv-lib">Guardar</button>
              <button onClick={() => setShowSaveEquivLib(false)} className="px-4 border border-brand-border text-brand-muted rounded-full py-2.5 text-sm">Não, obrigado</button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Versions modals */}
      {showVersionsModal && (
        <VersionHistoryModal
          plan={sp}
          activeVersionId={activeVersionId}
          isNutri={isNutri}
          onClose={() => setShowVersionsModal(false)}
          onSwitch={switchVersion}
          onChanged={async () => {
            try {
              const { data } = await axios.get(`${API}/api/meal-plans?client_id=${sp.client_id}&version_id=${encodeURIComponent(activeVersionId)}`);
              const fresh = (data || []).find(p => p.id === sp.id);
              if (fresh) setSp(fresh);
            } catch {}
            if (onUpdate) onUpdate();
          }}
        />
      )}
      {showCompareModal && (
        <CompareVersionsModal plan={sp} onClose={() => setShowCompareModal(false)} />
      )}
      {showCreateVersion && (
        <CreateVersionModal
          plan={sp}
          activeVersionId={activeVersionId}
          onClose={() => setShowCreateVersion(false)}
          onCreated={async (newVersionId) => {
            setShowCreateVersion(false);
            try {
              const { data } = await axios.get(`${API}/api/meal-plans?client_id=${sp.client_id}&version_id=${encodeURIComponent(newVersionId)}`);
              const fresh = (data || []).find(p => p.id === sp.id);
              if (fresh) { setSp(fresh); setActiveVersionId(newVersionId); }
            } catch {}
            toast.success('Nova versão criada');
            if (onUpdate) onUpdate();
          }}
        />
      )}
    </div>
  );
}

// ─── PLAN VERSIONS UI ───

// Item 8 Fase 5 — Painel de versões arquivadas (histórico)
function GeneralNotesCard({ plan, isNutri, onSave }) {
  const [txt, setTxt] = useState(plan.general_notes || '');
  useEffect(() => { setTxt(plan.general_notes || ''); }, [plan.general_notes, plan.active_version_id]);
  if (!isNutri && !(plan.general_notes || '').trim()) return null;
  return (
    <div className="bg-white border border-brand-border rounded-3xl p-4 mb-5" data-testid="plan-general-notes">
      <h3 className="font-heading font-medium text-xs text-brand-muted uppercase tracking-wider mb-2">Notas gerais</h3>
      {isNutri ? (
        <><textarea value={txt} onChange={e => setTxt(e.target.value)} onBlur={() => { if (txt !== (plan.general_notes || '')) onSave({ general_notes: txt }); }} rows={3} placeholder="Notas gerais deste plano (visíveis para a paciente e no PDF)" className="w-full text-sm border border-brand-border rounded-xl p-3 outline-none focus:border-brand-green" data-testid="plan-general-notes-input" />
        <div className="mt-2"><InsertImageButton testId="plan-notes-insert-image" onPick={id => { const t = `${txt}${txt && !txt.endsWith('\n') ? '\n' : ''}[[img:${id}]]\n`; setTxt(t); onSave({ general_notes: t }); }} /></div>
        {/\[\[img:/.test(txt) && <RichNote text={txt} className="mt-2 text-sm text-brand-text" />}</>
      ) : <RichNote text={plan.general_notes} className="text-sm text-brand-text" />}
    </div>
  );
}

function ArchivedVersionsPanel({ plan, isNutri, isClient, onPlanChanged }) {
  const confirmDelete = useConfirmDelete();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showToClient, setShowToClient] = useState(!!plan?.show_versions_to_client);

  useEffect(() => { setShowToClient(!!plan?.show_versions_to_client); }, [plan?.show_versions_to_client]);

  const fetchArchives = useCallback(async () => {
    if (!plan?.id) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/meal-plans/${plan.id}/archives`);
      setItems(Array.isArray(data) ? data : []);
    } catch {
      setItems([]);
    } finally { setLoading(false); }
  }, [plan?.id]);

  useEffect(() => { if (open) fetchArchives(); }, [open, fetchArchives]);

  const toggleShowToClient = async () => {
    const next = !showToClient;
    try {
      await axios.put(`${API}/api/meal-plans/${plan.id}/show-versions-to-client`, { show: next });
      setShowToClient(next);
      if (onPlanChanged) onPlanChanged({ ...plan, show_versions_to_client: next });
      toast.success(next ? 'Visível para a cliente' : 'Apenas visível para ti');
    } catch { toast.error('Erro ao atualizar visibilidade'); }
  };

  const downloadArchivePDF = async (archiveId, label) => {
    try {
      const { data } = await axios.get(`${API}/api/meal-plans/${plan.id}/pdf?archive_id=${archiveId}&version=client`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(data);
      const a = document.createElement('a');
      a.href = url;
      const safe = (label || 'plano').replace(/[^a-zA-Z0-9-_]/g, '_');
      a.download = `${safe}_arquivo.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error('Erro ao gerar PDF'); }
  };

  const deleteArchive = async (archiveId) => {
    const ok = await confirmDelete({ description: 'Vai apagar esta versão arquivada. Esta ação é irreversível.' });
    if (!ok) return;
    try {
      await axios.delete(`${API}/api/meal-plans/${plan.id}/archives/${archiveId}`);
      toast.success('Versão eliminada');
      fetchArchives();
    } catch { toast.error('Erro ao eliminar'); }
  };

  // Client cannot see panel if toggle is off
  if (isClient && !plan?.show_versions_to_client) return null;

  return (
    <div className="bg-brand-bg/30 border border-brand-border rounded-2xl mb-4" data-testid="archived-versions-panel">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between p-3 text-left" data-testid="archived-versions-toggle">
        <div>
          <p className="text-[11px] uppercase tracking-wider text-brand-muted">Versões anteriores</p>
          <p className="text-xs text-brand-text mt-0.5">{open ? 'Ocultar histórico' : 'Ver histórico de alterações ao plano'}</p>
        </div>
        <span className="text-brand-muted text-xs">{open ? '▲' : '▼'}</span>
      </button>
      {open && (
        <div className="border-t border-brand-border/50 p-3 space-y-2">
          {isNutri && (
            <label className="flex items-center gap-2 text-xs text-brand-text mb-2 cursor-pointer select-none" data-testid="show-versions-toggle">
              <input type="checkbox" checked={showToClient} onChange={toggleShowToClient} className="rounded" data-testid="show-versions-checkbox" />
              <span>Mostrar histórico à cliente</span>
            </label>
          )}
          {loading ? (
            <p className="text-xs text-brand-muted text-center py-3">A carregar...</p>
          ) : items.length === 0 ? (
            <p className="text-xs text-brand-muted text-center py-3">Sem versões arquivadas. Cada vez que guardas alterações ao plano, a versão anterior fica aqui registada.</p>
          ) : (
            <ul className="space-y-1.5">
              {items.map(it => {
                const t = it.daily_totals || {};
                const dt = it.archived_at ? new Date(it.archived_at) : null;
                return (
                  <li key={it.id} className="bg-white border border-brand-border rounded-xl px-3 py-2 flex items-center justify-between gap-2 flex-wrap" data-testid={`archive-${it.id}`}>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-brand-text truncate">{it.name || 'Plano'}</p>
                      <p className="text-[10px] text-brand-muted">
                        {dt ? `Arquivada em ${fmtStamp(dt.toISOString())}` : '-'}
                      </p>
                      <p className="text-[10px] text-brand-muted">
                        <span className="text-brand-green font-medium">{Math.round(t.energia_kcal || 0)} kcal</span>
                        {` · P ${Math.round(t.proteina_g || 0)}g · HC ${Math.round(t.hidratos_g || 0)}g · G ${Math.round(t.gordura_g || 0)}g`}
                      </p>
                    </div>
                    <div className="flex items-center gap-1">
                      <button onClick={() => downloadArchivePDF(it.id, it.name)} className="text-[10px] border border-brand-border text-brand-green hover:bg-brand-green/5 rounded-full px-2.5 py-1 flex items-center gap-1" data-testid={`archive-pdf-${it.id}`} title="Descarregar PDF">
                        <FilePdf size={11} /> PDF
                      </button>
                      {isNutri && (
                        <button onClick={() => deleteArchive(it.id)} className="p-1.5 text-brand-placeholder hover:text-red-500" data-testid={`archive-delete-${it.id}`} title="Eliminar">
                          <Trash size={12} />
                        </button>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

function PlanVersionsBar({ plan, activeVersionId, isNutri, isClient, onSwitch, onOpenHistory, onOpenCompare, onOpenCreate }) {
  const versions = Array.isArray(plan?.versions) ? plan.versions : [];
  if (!versions.length) return null;
  const activeLabel = (versions.find(v => v.version_id === activeVersionId) || {}).label || 'Principal';
  // Client mode: only show pills if there are 2+ versions
  if (isClient) {
    if (versions.length < 2) return null;
    return (
      <div className="flex flex-wrap items-center gap-1.5 mb-4" data-testid="client-version-pills">
        <span className="text-[11px] text-brand-muted mr-1">Versão:</span>
        {versions.map(v => (
          <button
            key={v.version_id}
            onClick={() => onSwitch(v.version_id)}
            className={`px-3 py-1.5 rounded-full text-xs transition-all ${activeVersionId === v.version_id ? 'bg-brand-green text-white' : 'bg-brand-bg text-brand-muted border border-brand-border hover:border-brand-green'}`}
            data-testid={`client-version-${v.version_id}`}
          >{v.label}</button>
        ))}
      </div>
    );
  }
  // Nutri mode: show pills + management buttons
  if (!isNutri) return null;
  return (
    <div className="bg-brand-bg/40 border border-brand-border rounded-2xl p-3 mb-4 flex flex-wrap items-center gap-2" data-testid="nutri-versions-bar">
      <span className="text-[11px] uppercase tracking-wider text-brand-placeholder mr-1">A editar:</span>
      <div className="flex flex-wrap gap-1.5">
        {versions.map(v => (
          <button
            key={v.version_id}
            onClick={() => onSwitch(v.version_id)}
            className={`px-2.5 py-1 rounded-full text-xs transition-all ${activeVersionId === v.version_id ? 'bg-brand-green text-white' : 'bg-white text-brand-muted border border-brand-border hover:border-brand-green'}`}
            data-testid={`nutri-version-${v.version_id}`}
            title={v.change_reason || ''}
          >
            {v.label}{v.is_default ? ' ★' : ''}
          </button>
        ))}
      </div>
      <div className="ml-auto flex flex-wrap items-center gap-1.5">
        <button onClick={onOpenCreate} className="text-xs text-brand-green hover:underline" data-testid="version-create-btn">+ Nova versão</button>
        <span className="text-brand-placeholder">·</span>
        <button onClick={onOpenHistory} className="text-xs text-brand-muted hover:text-brand-green" data-testid="version-history-btn">Histórico</button>
        {versions.length >= 2 && (
          <>
            <span className="text-brand-placeholder">·</span>
            <button onClick={onOpenCompare} className="text-xs text-brand-muted hover:text-brand-green" data-testid="version-compare-btn">Comparar</button>
          </>
        )}
        <span className="sr-only">{activeLabel}</span>
      </div>
    </div>
  );
}

function VersionHistoryModal({ plan, activeVersionId, isNutri, onClose, onSwitch, onChanged }) {
  const confirmDelete = useConfirmDelete();
  const versions = Array.isArray(plan?.versions) ? plan.versions : [];
  const setDefault = async (vid) => {
    try { await axios.post(`${API}/api/meal-plans/${plan.id}/versions/${vid}/set-default`); toast.success('Versão definida por defeito'); onChanged(); } catch (e) { toast.error(e.response?.data?.detail || 'Erro'); }
  };
  const remove = async (vid) => {
    const ok = await confirmDelete({ description: 'Vai apagar esta versão. O histórico anterior é preservado nas outras versões.' });
    if (!ok) return;
    try { await axios.delete(`${API}/api/meal-plans/${plan.id}/versions/${vid}`); toast.success('Versão eliminada'); onChanged(); } catch (e) { toast.error(e.response?.data?.detail || 'Erro'); }
  };
  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="rounded-3xl border-brand-border max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-heading text-brand-text">Histórico de versões</DialogTitle></DialogHeader>
        <div className="space-y-2" data-testid="version-history-list">
          {versions.map(v => (
            <div key={v.version_id} className={`border rounded-2xl p-3 ${activeVersionId === v.version_id ? 'border-brand-green bg-brand-green/5' : 'border-brand-border bg-white'}`} data-testid={`version-row-${v.version_id}`}>
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-brand-text">
                    {v.label}
                    {v.is_default && <span className="ml-2 text-[10px] bg-brand-green/10 text-brand-green px-2 py-0.5 rounded-full">Por defeito</span>}
                  </p>
                  <p className="text-[11px] text-brand-muted mt-0.5">
                    {v.created_at ? fmtDay(v.created_at) : '-'}
                    {v.change_reason ? ` · ${v.change_reason}` : ''}
                  </p>
                </div>
                <div className="flex items-center gap-1.5">
                  <button onClick={() => { onSwitch(v.version_id); onClose(); }} className="text-xs text-brand-green hover:underline" data-testid={`version-open-${v.version_id}`}>Abrir</button>
                  {isNutri && !v.is_default && (
                    <>
                      <button onClick={() => setDefault(v.version_id)} className="text-xs text-brand-muted hover:text-brand-green" data-testid={`version-set-default-${v.version_id}`}>Definir por defeito</button>
                      <button onClick={() => remove(v.version_id)} className="text-xs text-brand-muted hover:text-red-500" data-testid={`version-delete-${v.version_id}`}>Eliminar</button>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function CompareVersionsModal({ plan, onClose }) {
  const versions = Array.isArray(plan?.versions) ? plan.versions : [];
  const [a, setA] = useState(versions[0]?.version_id || '');
  const [b, setB] = useState((versions[1] || versions[0])?.version_id || '');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!a || !b || a === b) { setData(null); return; }
    setLoading(true);
    axios.get(`${API}/api/meal-plans/${plan.id}/versions/compare?a=${encodeURIComponent(a)}&b=${encodeURIComponent(b)}`)
      .then(({ data }) => setData(data)).catch(() => setData(null)).finally(() => setLoading(false));
  }, [a, b, plan.id]);
  const fmt = (n) => Math.round((n ?? 0) * 10) / 10;
  const dailyRow = (label, key, unit) => {
    const va = fmt(data?.a?.daily?.[key]);
    const vb = fmt(data?.b?.daily?.[key]);
    const diff = fmt((data?.b?.daily?.[key] ?? 0) - (data?.a?.daily?.[key] ?? 0));
    return (
      <div key={key} className="grid grid-cols-4 gap-2 text-[11px] py-1 border-b border-brand-border/40 last:border-0">
        <span className="text-brand-muted">{label}</span>
        <span className="text-brand-text">{va}{unit}</span>
        <span className="text-brand-text">{vb}{unit}</span>
        <span className={`font-medium ${diff > 0 ? 'text-brand-green' : diff < 0 ? 'text-amber-600' : 'text-brand-muted'}`}>{diff > 0 ? '+' : ''}{diff}{unit}</span>
      </div>
    );
  };
  // Build merged meal map (union of keys from both versions)
  const mealKeys = useMemo(() => {
    const ks = new Set([...Object.keys(data?.a?.by_meal || {}), ...Object.keys(data?.b?.by_meal || {})]);
    return Array.from(ks);
  }, [data]);
  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="rounded-3xl border-brand-border max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-heading text-brand-text">Comparar versões</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3 mb-3">
          <div>
            <Label className="text-[11px] uppercase text-brand-placeholder mb-1 block">Versão A</Label>
            <select value={a} onChange={e => setA(e.target.value)} className="w-full rounded-xl border border-brand-border text-xs p-2" data-testid="compare-select-a">
              {versions.map(v => <option key={v.version_id} value={v.version_id}>{v.label}</option>)}
            </select>
          </div>
          <div>
            <Label className="text-[11px] uppercase text-brand-placeholder mb-1 block">Versão B</Label>
            <select value={b} onChange={e => setB(e.target.value)} className="w-full rounded-xl border border-brand-border text-xs p-2" data-testid="compare-select-b">
              {versions.map(v => <option key={v.version_id} value={v.version_id}>{v.label}</option>)}
            </select>
          </div>
        </div>
        {a === b && <p className="text-xs text-amber-600">Escolhe duas versões diferentes.</p>}
        {loading && <p className="text-xs text-brand-muted">A calcular…</p>}
        {data && a !== b && (
          <div className="space-y-4" data-testid="compare-result">
            <div className="bg-brand-bg/30 rounded-2xl p-3 border border-brand-border/60">
              <p className="text-[10px] uppercase tracking-wider text-brand-placeholder mb-1">Totais diários</p>
              <div className="grid grid-cols-4 gap-2 text-[10px] uppercase tracking-wider text-brand-placeholder pb-1 border-b border-brand-border/60">
                <span>Macro</span><span>{data.a.label}</span><span>{data.b.label}</span><span>Δ B−A</span>
              </div>
              {dailyRow('Energia', 'energia_kcal', ' kcal')}
              {dailyRow('Proteína', 'proteina_g', ' g')}
              {dailyRow('HC', 'hidratos_g', ' g')}
              {dailyRow('Gordura', 'gordura_g', ' g')}
              {dailyRow('Fibra', 'fibra_g', ' g')}
            </div>
            <div className="bg-white rounded-2xl p-3 border border-brand-border">
              <p className="text-[10px] uppercase tracking-wider text-brand-placeholder mb-1">Por refeição (kcal)</p>
              {mealKeys.length === 0 && <p className="text-xs text-brand-muted">Sem refeições.</p>}
              {mealKeys.map(mk => {
                const ma = data.a.by_meal?.[mk];
                const mb = data.b.by_meal?.[mk];
                const ka = fmt(ma?.totals?.energia_kcal);
                const kb = fmt(mb?.totals?.energia_kcal);
                const diff = fmt(kb - ka);
                const label = mb?.label || ma?.label || mk;
                return (
                  <div key={mk} className="grid grid-cols-4 gap-2 text-[11px] py-1 border-b border-brand-border/40 last:border-0">
                    <span className="text-brand-muted truncate">{label}</span>
                    <span className="text-brand-text">{ma ? `${ka} kcal` : '-'}</span>
                    <span className="text-brand-text">{mb ? `${kb} kcal` : '-'}</span>
                    <span className={`font-medium ${diff > 0 ? 'text-brand-green' : diff < 0 ? 'text-amber-600' : 'text-brand-muted'}`}>{diff > 0 ? '+' : ''}{diff} kcal</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function CreateVersionModal({ plan, activeVersionId, onClose, onCreated }) {
  const versions = Array.isArray(plan?.versions) ? plan.versions : [];
  const [label, setLabel] = useState('');
  const [reason, setReason] = useState('');
  const [mode, setMode] = useState('duplicate'); // 'duplicate' | 'blank'
  const [sourceId, setSourceId] = useState(activeVersionId || versions[0]?.version_id || '');
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!label.trim()) { toast.error('Etiqueta obrigatória'); return; }
    setSaving(true);
    try {
      const body = { label: label.trim(), change_reason: reason.trim() };
      if (mode === 'duplicate' && sourceId) body.source_version_id = sourceId;
      const { data } = await axios.post(`${API}/api/meal-plans/${plan.id}/versions`, body);
      onCreated(data?.version_id);
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro'); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="rounded-3xl border-brand-border">
        <DialogHeader><DialogTitle className="font-heading text-brand-text">Nova versão do plano</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-[11px] uppercase text-brand-placeholder mb-1 block">Etiqueta *</Label>
            <Input value={label} onChange={e => setLabel(e.target.value)} placeholder="ex: Fim de semana" className="rounded-xl border-brand-border text-sm" data-testid="version-create-label" />
          </div>
          <div>
            <Label className="text-[11px] uppercase text-brand-placeholder mb-1 block">Motivo da alteração (opcional)</Label>
            <Textarea value={reason} onChange={e => setReason(e.target.value)} placeholder="ex: ajuste pós-consulta de 08/05" rows={2} className="rounded-xl border-brand-border text-sm resize-none" data-testid="version-create-reason" />
          </div>
          <div>
            <Label className="text-[11px] uppercase text-brand-placeholder mb-1 block">Ponto de partida</Label>
            <div className="flex flex-wrap gap-1.5 mb-2">
              <button onClick={() => setMode('duplicate')} className={`px-3 py-1.5 rounded-full text-xs ${mode === 'duplicate' ? 'bg-brand-green text-white' : 'bg-white border border-brand-border text-brand-muted'}`} data-testid="version-mode-duplicate">Duplicar versão existente</button>
              <button onClick={() => setMode('blank')} className={`px-3 py-1.5 rounded-full text-xs ${mode === 'blank' ? 'bg-brand-green text-white' : 'bg-white border border-brand-border text-brand-muted'}`} data-testid="version-mode-blank">Começar do zero</button>
            </div>
            {mode === 'duplicate' && (
              <select value={sourceId} onChange={e => setSourceId(e.target.value)} className="w-full rounded-xl border border-brand-border text-xs p-2" data-testid="version-create-source">
                {versions.map(v => <option key={v.version_id} value={v.version_id}>{v.label}{v.is_default ? ' ★' : ''}</option>)}
              </select>
            )}
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <button onClick={onClose} className="text-xs text-brand-muted hover:text-brand-text px-3 py-2" data-testid="version-create-cancel">Cancelar</button>
            <button onClick={submit} disabled={saving} className="bg-brand-green text-white rounded-full px-4 py-2 text-xs font-medium disabled:opacity-50" data-testid="version-create-submit">{saving ? 'A criar…' : 'Criar versão'}</button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── INLINE PLAN NAME EDITOR ───
function InlinePlanName({ plan, onRename, onClick }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(plan.name);
  const save = async () => {
    if (val.trim() && val.trim() !== plan.name) {
      await onRename(plan.id, val.trim());
    }
    setEditing(false);
  };
  if (editing) {
    return (
      <input
        value={val} onChange={e => setVal(e.target.value)}
        onBlur={save}
        onKeyDown={e => { if (e.key === 'Enter') save(); if (e.key === 'Escape') { setVal(plan.name); setEditing(false); } }}
        onClick={e => e.stopPropagation()}
        className="font-heading font-medium text-brand-text border-b-2 border-brand-green bg-transparent outline-none w-full"
        autoFocus data-testid={`inline-rename-${plan.id}`}
      />
    );
  }
  return (
    <h3 className="font-heading font-medium text-brand-text group">
      {plan.name}
      <button onClick={e => { e.stopPropagation(); setVal(plan.name); setEditing(true); }} className="ml-1.5 text-brand-placeholder group-hover:text-brand-green opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`rename-btn-${plan.id}`}>
        <PencilSimple size={12} />
      </button>
    </h3>
  );
}

// ─── MAIN ───
export default function PlanosPage() {
  const { user } = useAuth();
  const [plans, setPlans] = useState([]);
  const [sp, setSp] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [planName, setPlanName] = useState('');
  const [clientId, setClientId] = useState('');
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mealTypes, setMealTypes] = useState([]);
  const [showImport, setShowImport] = useState(false);
  const [activeClientFilter, setActiveClientFilter] = useState('');
  const [planTemplates, setPlanTemplates] = useState([]);
  const [showFromTemplate, setShowFromTemplate] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templateClientId, setTemplateClientId] = useState('');
  const isNutri = user?.role==='admin'||user?.role==='nutricionista';

  const fetchPlans = useCallback(async()=>{
    try{
      const cid = activeClientFilter || '';
      const params = cid ? `?client_id=${cid}` : '';
      const{data}=await axios.get(`${API}/api/meal-plans${params}`);
      setPlans(data);
    }catch{}finally{setLoading(false);}
  }, [activeClientFilter]);
  const fetchClients = useCallback(async()=>{if(!isNutri)return;try{const{data}=await axios.get(`${API}/api/admin/users`);setClients(data.filter(u=>u.role==='cliente'));}catch{}}, [isNutri]);
  const fetchMealTypes = useCallback(async()=>{try{const{data}=await axios.get(`${API}/api/meal-types`);setMealTypes(data.types||[]);}catch{}}, []);
  const fetchTemplates = useCallback(async()=>{if(!isNutri)return;try{const{data}=await axios.get(`${API}/api/plan-templates`);setPlanTemplates(data.filter(t=>t.active));}catch{}}, [isNutri]);
  useEffect(()=>{fetchPlans();fetchClients();fetchMealTypes();fetchTemplates();},[fetchPlans,fetchClients,fetchMealTypes,fetchTemplates]);

  const handleCreate=async(e)=>{e.preventDefault();try{const{data}=await axios.post(`${API}/api/meal-plans`,{client_id:clientId,name:planName,status:'rascunho',meals:{}});toast.success('Plano criado');setShowCreate(false);setPlanName('');setClientId('');fetchPlans();setSp(data);}catch(err){toast.error(err.response?.data?.detail||'Erro');}};
  const handleRenamePlanInline = async (planId, newName) => {
    try { await axios.put(`${API}/api/meal-plans/${planId}`, { name: newName }); fetchPlans(); } catch { toast.error('Erro ao renomear'); }
  };  const handleCreateFromTemplate = async () => {
    if (!selectedTemplate || !templateClientId) { toast.error('Seleciona modelo e cliente'); return; }
    try {
      const { data } = await axios.post(`${API}/api/plan-templates/${selectedTemplate.id}/create-plan`, { client_id: templateClientId });
      toast.success('Plano criado a partir do modelo');
      setShowFromTemplate(false); setSelectedTemplate(null); setTemplateClientId('');
      fetchPlans(); setSp(data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro'); }
  };
  if(loading)return<div className="text-center py-12 text-brand-muted">A carregar...</div>;
  if(sp)return<PlanDetail key={sp.id} plan={sp} onBack={()=>{setSp(null);if(sp.client_id)setActiveClientFilter(sp.client_id);fetchPlans();}} onUpdate={fetchPlans} isNutri={isNutri} defaultMealTypes={mealTypes}/>;

  return (
    <div className="fade-in" data-testid="planos-page">
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-heading font-medium text-xl text-brand-text" data-testid="planos-title">Planos Alimentares</h2>
        {isNutri&&<div className="flex gap-2">
          <button onClick={()=>setShowImport(true)} className="border border-brand-border text-brand-muted rounded-full px-3 py-2.5 text-xs hover:bg-brand-bg" data-testid="import-plan-btn">Importar</button>
          {planTemplates.length > 0 && <button onClick={() => setShowFromTemplate(true)} className="border border-brand-border text-brand-green rounded-full px-3 py-2.5 text-xs hover:bg-brand-green/5" data-testid="from-template-btn">A partir de modelo</button>}
          <button onClick={()=>setShowCreate(true)} className="bg-brand-green text-white hover:bg-brand-green-hover rounded-full px-4 py-2.5 text-sm font-medium flex items-center gap-2" data-testid="create-plan-btn"><Plus size={16}/> Novo plano</button>
        </div>}
      </div>
      {/* Client filter */}
      {isNutri && clients.length > 0 && (
        <div className="mb-4 flex items-center gap-2">
          <select value={activeClientFilter} onChange={e => setActiveClientFilter(e.target.value)} className="border border-brand-border rounded-xl px-3 py-2 text-sm bg-white" data-testid="plan-client-filter">
            <option value="">Todos os clientes</option>
            {clients.map(c => <option key={c.user_id} value={c.user_id}>{c.name}</option>)}
          </select>
          {activeClientFilter && <button onClick={() => setActiveClientFilter('')} className="text-xs text-brand-green hover:underline">Limpar filtro</button>}
        </div>
      )}
      {plans.length===0?(
        <div className="text-center py-16"><ClipboardText size={48} className="mx-auto text-brand-placeholder mb-4" weight="duotone"/><p className="text-brand-muted">{isNutri?'Sem planos criados.':'Ainda não tem planos alimentares.'}</p></div>
      ):(
        <div className="space-y-3">
          {plans.map(p=>(
            <div key={p.id} onClick={()=>setSp(p)} className="w-full text-left bg-white border border-brand-border rounded-3xl p-4 shadow-[0_8px_32px_rgba(101,113,97,0.04)] hover:border-brand-green/30 transition-all cursor-pointer" role="button" tabIndex={0} onKeyDown={e => { if (e.key === 'Enter') setSp(p); }} data-testid={`plan-card-${p.id}`}>
              <div className="flex items-center justify-between">
                <div>
                  <InlinePlanName plan={p} onRename={handleRenamePlanInline} onClick={() => setSp(p)} />
                  <div className="flex flex-wrap items-center gap-2 mt-1">
                    <span className={`inline-block text-xs font-medium px-2.5 py-1 rounded-full ${p.status==='ativo'?'bg-brand-mint/20 text-brand-green':'bg-brand-bg text-brand-muted'}`}>{p.status==='ativo'?'Ativo':'Rascunho'}</span>
                    {isNutri && <PlanTargetBadge plan={p} />}
                  </div>
                </div>
                <PencilSimple size={16} className="text-brand-placeholder"/>
              </div>
            </div>
          ))}
        </div>
      )}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="rounded-3xl border-brand-border">
          <DialogHeader><DialogTitle className="font-heading text-brand-text">Novo Plano Alimentar</DialogTitle></DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4">
            <div><Label className="text-sm text-brand-muted mb-1.5 block">Nome</Label><Input value={planName} onChange={e=>setPlanName(e.target.value)} placeholder="Ex: Plano mensal" className="rounded-2xl border-brand-border" required data-testid="plan-name-input"/></div>
            <div><Label className="text-sm text-brand-muted mb-1.5 block">Cliente</Label><select value={clientId} onChange={e=>setClientId(e.target.value)} className="w-full border border-brand-border rounded-2xl px-3 py-2.5 text-sm bg-white" required data-testid="plan-client-select"><option value="">Selecionar...</option>{clients.map(c=><option key={c.user_id} value={c.user_id}>{c.name} ({c.email})</option>)}</select></div>
            <button type="submit" className="w-full bg-brand-green text-white rounded-full px-6 py-3.5 font-medium" data-testid="submit-create-plan">Criar plano</button>
          </form>
        </DialogContent>
      </Dialog>
      {isNutri && <ImportMealPlanDialog open={showImport} onOpenChange={setShowImport} patientId={clientId || user?.user_id} onImported={fetchPlans} />}
      {/* Create from template dialog */}
      <Dialog open={showFromTemplate} onOpenChange={setShowFromTemplate}>
        <DialogContent className="rounded-3xl border-brand-border">
          <DialogHeader><DialogTitle className="font-heading text-brand-text">Criar a partir de modelo</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs text-brand-muted mb-1 block">Modelo</Label>
              <div className="space-y-1.5 max-h-[200px] overflow-y-auto">
                {planTemplates.map(t => (
                  <button key={t.id} onClick={() => setSelectedTemplate(t)} className={`w-full text-left px-3 py-2 rounded-xl text-sm border ${selectedTemplate?.id === t.id ? 'border-brand-green bg-brand-green/5' : 'border-brand-border'}`} data-testid={`select-template-${t.id}`}>
                    {t.name}
                  </button>
                ))}
              </div>
            </div>
            <div><Label className="text-xs text-brand-muted mb-1 block">Cliente</Label>
              <PatientSearch value={templateClientId} onChange={v => setTemplateClientId(v)} />
            </div>
            <button onClick={handleCreateFromTemplate} className="w-full bg-brand-green text-white rounded-full py-3 font-medium" data-testid="confirm-from-template">Criar plano</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
